import {
  ANY_SUBMITTED,
  ANY_SUBMITTED_LABEL,
  CLAIMS_FILTER,
  DATE_PRESET,
  DATE_PRESET_OPTIONS,
  DEFAULT_DATE_PRESET,
  ELIGIBILITY_STATUS,
  ESTIMATE_MARK,
  LEDGER_SOURCE_TYPE,
  LEDGER_TYPE,
  MOVE_COST_SOURCE_LABEL,
  PLACEHOLDER,
  SUBMISSION_LABELS,
  SUBMISSION_STATUS,
  SUBMITTED_STATUSES,
  UNKNOWN_ROOFTOP_ID,
  UNKNOWN_ROOFTOP_NAME,
  accessorialName,
  byRooftopName,
  describeVehicle,
  formatCount,
  formatCountOf,
  formatCurrency,
  formatDate,
  formatDistance,
  formatOdometer,
  groupLedgerBySource,
  isLedgerEstimated,
  ledgerAmount,
  ledgerSourceLabel,
  legTotal,
  toAmount
} from "./chunk-QBQOJXLM.mjs";

// src/useProgramClaimsSearch.ts
import { useCallback, useEffect, useMemo, useState } from "react";

// src/utils/claimsWhere.ts
import { LANE_TOLL_ACCESSORIAL_ID, RULE_OUTCOMES, TOLL_ACCESSORIAL_CODE } from "@hopdrive/sdk-ar";
var NUMERIC_TERM = /^\d+$/;
var WORD_BREAK = /\s+/g;
var CODE_WORD_BREAK = "_";
var LANE_TOLL_MIN_TERM = 3;
var containing = (term) => ({ _ilike: `%${term.replace(/[\\%_]/g, (match) => `\\${match}`)}%` });
var asCode = (term) => term.toLowerCase().replace(WORD_BREAK, CODE_WORD_BREAK);
var chargeConditions = (term) => {
  const conditions = [{ program_ledger: { move: { accessorials: { code: containing(asCode(term)) } } } }];
  const code = asCode(term);
  if (code.length >= LANE_TOLL_MIN_TERM && TOLL_ACCESSORIAL_CODE.startsWith(code)) {
    conditions.push({
      program_ledger: {
        source_type: { _eq: LEDGER_SOURCE_TYPE.ACCESSORIAL },
        source_id: { _eq: String(LANE_TOLL_ACCESSORIAL_ID) }
      }
    });
  }
  return conditions;
};
var searchConditions = (term) => {
  const text2 = containing(term);
  const conditions = [
    { move: { reference_num: text2 } },
    { move: { customer: { name: text2 } } },
    { move: { consumer_name: text2 } },
    { move: { vehicle_make: text2 } },
    { move: { vehicle_model: text2 } },
    { move: { vehicle_vin: text2 } },
    ...chargeConditions(term)
  ];
  if (NUMERIC_TERM.test(term)) {
    const id = Number(term);
    conditions.push({ id: { _eq: id } }, { move_id: { _eq: id } }, { scenario_id: { _eq: id } });
  }
  return conditions;
};
var submissionStatusesFor = (value) => {
  if (!value) return null;
  return value === ANY_SUBMITTED ? SUBMITTED_STATUSES : [value];
};
var sourceConditions = (label) => {
  const pattern = label.trim().replace(/[\\%_]/g, (match) => `\\${match}`).replace(WORD_BREAK, "_");
  const conditions = [
    { source_type: { _eq: LEDGER_SOURCE_TYPE.ACCESSORIAL }, move: { accessorials: { code: { _ilike: pattern } } } }
  ];
  if (asCode(label) === TOLL_ACCESSORIAL_CODE) {
    conditions.push({ source_type: { _eq: LEDGER_SOURCE_TYPE.ACCESSORIAL }, source_id: { _eq: String(LANE_TOLL_ACCESSORIAL_ID) } });
  }
  return conditions;
};
var sourcesWhere = (sources) => {
  const named = sources.filter((source) => source !== MOVE_COST_SOURCE_LABEL);
  if (!named.length) return null;
  return { program_ledger: { _or: named.flatMap(sourceConditions) } };
};
var rooftopWhere = (rooftopId) => rooftopId === UNKNOWN_ROOFTOP_ID ? { _not: { move: { customer_id: { _is_null: false } } } } : { move: { customer_id: { _eq: rooftopId } } };
var andWhere = (where, ...also) => {
  const own = Array.isArray(where._and) && Object.keys(where).length === 1 ? where._and : [where];
  return { _and: [...own, ...also] };
};
function buildProgramClaimsWhere(scope, criteria = {}) {
  const conditions = [scope];
  const { eligibilityStatuses, submissionStatuses, failedRule, customerIds, range } = criteria;
  const term = criteria.search?.trim() ?? "";
  if (eligibilityStatuses?.length) conditions.push({ eligibility_status: { _in: [...eligibilityStatuses] } });
  if (submissionStatuses?.length) conditions.push({ submission_status: { _in: [...submissionStatuses] } });
  if (failedRule) conditions.push({ evaluation_result: { _contains: [{ ruleKey: failedRule, outcome: RULE_OUTCOMES.FAIL }] } });
  if (customerIds?.length) conditions.push({ move: { customer_id: { _in: [...customerIds] } } });
  if (range?.start || range?.end) {
    const pickupTime = {};
    if (range.start) pickupTime._gte = range.start;
    if (range.end) pickupTime._lte = range.end;
    conditions.push({ move: { pickup_time: pickupTime } });
  }
  const sources = sourcesWhere(criteria.sources ?? []);
  if (sources) conditions.push(sources);
  if (term) conditions.push({ _or: searchConditions(term) });
  return conditions.length === 1 ? scope : { _and: conditions };
}

// src/utils/dateRange.ts
var DAY_MS = 24 * 60 * 60 * 1e3;
var START_OF_DAY = "T00:00:00";
var END_OF_DAY = "T23:59:59";
function resolveDateRange(preset, customStart, customEnd, now = Date.now()) {
  if (preset === DATE_PRESET.CUSTOM) {
    return {
      start: customStart ? (/* @__PURE__ */ new Date(`${customStart}${START_OF_DAY}`)).toISOString() : null,
      end: customEnd ? (/* @__PURE__ */ new Date(`${customEnd}${END_OF_DAY}`)).toISOString() : null
    };
  }
  const days = DATE_PRESET_OPTIONS.find((option2) => option2.value === preset)?.days ?? 0;
  return { start: new Date(now - days * DAY_MS).toISOString(), end: null };
}

// src/utils/ledgerViews.ts
var LEDGER_VIEW = {
  HOPDRIVE: "hopdrive",
  DEALER: "dealer"
};
var MONEY_COLUMN = {
  COVERED: "covered",
  PAID_BACK: "paid_back",
  TOTAL_MOVE_COST: "total_move_cost",
  REIMBURSED: "reimbursed"
};
var coveredTotal = (ledger) => legTotal(ledger, LEDGER_TYPE.PROGRAM_PAYMENT, (row) => toAmount(row.program_max));
var paidBackTotal = (ledger) => legTotal(ledger, LEDGER_TYPE.RECOUP, ledgerAmount);
var reimbursedTotal = (ledger) => legTotal(ledger, LEDGER_TYPE.REIMBURSEMENT, ledgerAmount);
var totalMoveCost = (ledger) => {
  const covered = coveredTotal(ledger);
  const paidBack = paidBackTotal(ledger);
  return covered === null && paidBack === null ? null : (covered ?? 0) + (paidBack ?? 0);
};
var totalMoveCostOfSums = ({ covered, paidBack }) => covered === null && paidBack === null ? null : (covered ?? 0) + (paidBack ?? 0);
var COVERED = { key: MONEY_COLUMN.COVERED, label: "Covered", total: coveredTotal, fromSums: (sums) => sums.covered };
var PAID_BACK = { key: MONEY_COLUMN.PAID_BACK, total: paidBackTotal, fromSums: (sums) => sums.paidBack };
var REIMBURSED = { key: MONEY_COLUMN.REIMBURSED, total: reimbursedTotal, fromSums: (sums) => sums.reimbursed };
var option = (value, label, statuses = [value]) => ({
  value,
  label,
  statuses
});
var HOPDRIVE_LEDGER_VIEW = {
  key: LEDGER_VIEW.HOPDRIVE,
  columns: [
    COVERED,
    { ...PAID_BACK, label: "Expected payment" },
    { key: MONEY_COLUMN.TOTAL_MOVE_COST, label: "Total move cost", total: totalMoveCost, fromSums: totalMoveCostOfSums },
    { ...REIMBURSED, label: "Expected reimbursement" }
  ],
  eligibilityOptions: [
    option(ELIGIBILITY_STATUS.ELIGIBLE, "Eligible"),
    option(ELIGIBILITY_STATUS.INELIGIBLE, "Ineligible"),
    option(ELIGIBILITY_STATUS.OVERRIDE_ELIGIBLE, "Override eligible")
  ],
  marksEstimates: false,
  estimateNote: "* Eligibility is HopDrive's best estimate of coverage until the claim is submitted to the program."
};
var DEALER_LEDGER_VIEW = {
  key: LEDGER_VIEW.DEALER,
  columns: [
    COVERED,
    { ...PAID_BACK, label: "You may owe" },
    { ...REIMBURSED, label: "Reimbursement" }
  ],
  eligibilityOptions: [
    option(ELIGIBILITY_STATUS.ELIGIBLE, "Eligible", [
      ELIGIBILITY_STATUS.ELIGIBLE,
      ELIGIBILITY_STATUS.OVERRIDE_ELIGIBLE
    ]),
    option(ELIGIBILITY_STATUS.INELIGIBLE, "Ineligible")
  ],
  marksEstimates: true,
  estimateNote: "* Eligibility is an estimate until the claim is submitted to the program, and amounts are estimates until the program confirms them."
};
var claimTotals = /* @__PURE__ */ new WeakMap();
var claimColumnTotal = (column2, claim) => {
  let totals = claimTotals.get(claim);
  if (!totals) {
    totals = /* @__PURE__ */ new Map();
    claimTotals.set(claim, totals);
  }
  if (!totals.has(column2.key)) totals.set(column2.key, column2.total(claim.program_ledger));
  return totals.get(column2.key) ?? null;
};
var eligibilityLabel = (view, status) => {
  if (!status) return "";
  return view.eligibilityOptions.find((candidate) => candidate.statuses.includes(status))?.label ?? status;
};
var eligibilityStatusesFor = (view, value) => value ? view.eligibilityOptions.find((candidate) => candidate.value === value)?.statuses ?? [value] : null;
var ledgerLegDescription = (type, programName) => {
  const program = programName || "the program";
  switch (type) {
    case LEDGER_TYPE.PROGRAM_PAYMENT:
      return `Covered by ${program}`;
    case LEDGER_TYPE.RECOUP:
      return `Expected payment to ${program}`;
    case LEDGER_TYPE.REIMBURSEMENT:
      return `Expected reimbursement from ${program}`;
    default:
      return type;
  }
};

// src/useProgramClaimsSearch.ts
var SEARCH_DEBOUNCE_MS = 300;
var DEFAULT_ROWS_PER_PAGE = 25;
function useProgramClaimsSearch({ scope, view }) {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [eligibility, setEligibility] = useState(null);
  const [submission, setSubmission] = useState(null);
  const [failedRule, setFailedRule] = useState(null);
  const [customerIds, setCustomerIds] = useState([]);
  const [sources, setSources] = useState([]);
  const [datePreset, setDatePreset] = useState(DEFAULT_DATE_PRESET);
  const [customStart, setCustomStart] = useState(null);
  const [customEnd, setCustomEnd] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPageState] = useState(DEFAULT_ROWS_PER_PAGE);
  const [refreshes, setRefreshes] = useState(0);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);
  const range = useMemo(
    () => resolveDateRange(datePreset, customStart, customEnd),
    [datePreset, customStart, customEnd, refreshes]
  );
  const scopeKey = JSON.stringify(scope);
  const stableScope = useMemo(() => JSON.parse(scopeKey), [scopeKey]);
  const sourceOptionsWhere = useMemo(
    () => buildProgramClaimsWhere(stableScope, {
      search: debouncedSearch,
      submissionStatuses: submissionStatusesFor(submission),
      failedRule,
      customerIds,
      range
    }),
    [stableScope, debouncedSearch, submission, failedRule, customerIds, range]
  );
  const tilesWhere = useMemo(() => {
    const booked = sourcesWhere(sources);
    return booked ? andWhere(sourceOptionsWhere, booked) : sourceOptionsWhere;
  }, [sourceOptionsWhere, sources]);
  const where = useMemo(() => {
    const statuses = eligibilityStatusesFor(view, eligibility);
    return statuses ? andWhere(tilesWhere, { eligibility_status: { _in: [...statuses] } }) : tilesWhere;
  }, [tilesWhere, view, eligibility]);
  useEffect(() => {
    setPage(0);
  }, [scopeKey, debouncedSearch, eligibility, submission, failedRule, customerIds, sources, datePreset, customStart, customEnd]);
  const refresh = useCallback(() => setRefreshes((count2) => count2 + 1), []);
  const setRowsPerPage = useCallback((value) => {
    setRowsPerPageState(value);
    setPage(0);
  }, []);
  const resetDates = useCallback(() => {
    setDatePreset(DEFAULT_DATE_PRESET);
    setCustomStart(null);
    setCustomEnd(null);
  }, []);
  const clearFilter = useCallback(
    (key) => {
      switch (key) {
        case CLAIMS_FILTER.SEARCH:
          setSearch("");
          setDebouncedSearch("");
          return;
        case CLAIMS_FILTER.ELIGIBILITY:
          return setEligibility(null);
        case CLAIMS_FILTER.SUBMISSION:
          return setSubmission(null);
        case CLAIMS_FILTER.FAILED_RULE:
          return setFailedRule(null);
        case CLAIMS_FILTER.CUSTOMERS:
          return setCustomerIds([]);
        case CLAIMS_FILTER.SOURCES:
          return setSources([]);
        case CLAIMS_FILTER.DATE:
          return resetDates();
      }
    },
    [resetDates]
  );
  const clearAllFilters = useCallback(() => {
    setSearch("");
    setDebouncedSearch("");
    setEligibility(null);
    setSubmission(null);
    setFailedRule(null);
    setCustomerIds([]);
    setSources([]);
    resetDates();
  }, [resetDates]);
  const choosePreset = useCallback((preset) => {
    setDatePreset(preset);
    if (preset !== DATE_PRESET.CUSTOM) {
      setCustomStart(null);
      setCustomEnd(null);
    }
  }, []);
  return {
    view,
    search,
    setSearch,
    eligibility,
    setEligibility,
    submission,
    setSubmission,
    failedRule,
    setFailedRule,
    customerIds,
    setCustomerIds,
    sources,
    setSources,
    datePreset,
    setDatePreset: choosePreset,
    customStart,
    setCustomStart,
    customEnd,
    setCustomEnd,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    range,
    scope: stableScope,
    where,
    tilesWhere,
    sourceOptionsWhere,
    refresh,
    refreshKey: refreshes,
    clearFilter,
    clearAllFilters
  };
}

// src/utils/failedRules.ts
import { NOT_EVALUATED_SOURCE, RULE_OUTCOMES as RULE_OUTCOMES2 } from "@hopdrive/sdk-ar";
var ELIGIBILITY_RULE_LABELS = {
  qualifying_make: "Eligible make",
  model_year_age: "Vehicle age",
  odometer_limit: "Odometer limit",
  first_year_owner: "First-year owner",
  full_vin: "Full VIN",
  vin_verified: "VIN verified at pickup",
  ro_number: "RO number",
  consumer_pickup_ping: "Consumer pickup ping",
  move_duration_in_bounds: "Move duration in bounds",
  pickup_location_in_bounds: "Pickup location in bounds",
  delivery_location_in_bounds: "Delivery location in bounds"
};
var humanize = (key) => {
  const words = key.replace(/[_-]+/g, " ").trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
};
var readVerdicts = (evaluationResult) => Array.isArray(evaluationResult) ? evaluationResult.flatMap(
  (entry) => typeof entry?.ruleKey === "string" && entry.ruleKey ? [{ key: entry.ruleKey, outcome: entry.outcome, source: entry.source }] : []
) : [];
var eligibilityRuleLabel = (key) => ELIGIBILITY_RULE_LABELS[key] ?? humanize(key);
var failedKeys = (verdicts) => new Set(verdicts.filter((verdict) => verdict.outcome === RULE_OUTCOMES2.FAIL).map((verdict) => verdict.key));
function failedRuleLabels(evaluationResult) {
  return [...failedKeys(readVerdicts(evaluationResult))].map(eligibilityRuleLabel);
}
var MISSING_FACT_WORDING = {
  qualifying_make: "not provided",
  model_year_age: "not provided",
  full_vin: "not provided",
  vin_verified: "not yet scanned",
  ro_number: "not provided",
  odometer_limit: "not yet recorded",
  consumer_pickup_ping: "not yet checked",
  move_duration_in_bounds: "not yet checked",
  pickup_location_in_bounds: "not yet checked",
  delivery_location_in_bounds: "not yet checked"
};
var MISSING_FACT_DEFAULT = "not yet known";
var LOCATION_RULE_KEYS = /* @__PURE__ */ new Set([
  "consumer_pickup_ping",
  "move_duration_in_bounds",
  "pickup_location_in_bounds",
  "delivery_location_in_bounds"
]);
var UNFILTERABLE_RULE_KEYS = /* @__PURE__ */ new Set(["first_year_owner", ...LOCATION_RULE_KEYS]);
var LOCATION_CHECKS_LABEL = "Meets geofencing requirements and data quality checks";
var LOCATION_CHECKS_WORDING = "not yet checked";
var PENDING_CHECK_SEPARATOR = ": ";
function pendingRuleChecks(evaluationResult) {
  const verdicts = readVerdicts(evaluationResult);
  const failed = failedKeys(verdicts);
  const keys = verdicts.filter((verdict) => verdict.outcome === RULE_OUTCOMES2.ASSUME && verdict.source !== NOT_EVALUATED_SOURCE && !failed.has(verdict.key)).map((verdict) => verdict.key);
  return [...new Set(keys)].flatMap((key) => {
    if (LOCATION_RULE_KEYS.has(key)) {
      return keys.find((k) => LOCATION_RULE_KEYS.has(k)) === key ? [{ label: LOCATION_CHECKS_LABEL, status: LOCATION_CHECKS_WORDING }] : [];
    }
    return [{ label: eligibilityRuleLabel(key), status: MISSING_FACT_WORDING[key] ?? MISSING_FACT_DEFAULT }];
  });
}
var pendingCheckLine = ({ label, status }) => `${label}${PENDING_CHECK_SEPARATOR}${status}`;
function atRiskRuleLabels(evaluationResult) {
  return pendingRuleChecks(evaluationResult).map(pendingCheckLine);
}

// src/utils/eligibilityRows.ts
import { ELIGIBILITY_RULE_TYPES, RULE_TIMING } from "@hopdrive/sdk-ar";
var ELIGIBILITY_ROW_COPY = {
  makeLabel: (makes) => `${makes} vehicle`,
  makeFailed: (entered) => `Make is ${entered}`,
  // The OEM's rule runs from the date of sale, which neither surface can see. The contract's
  // model-year limit can rule a vehicle out, never in, so the row only shows when it does; the
  // date of sale is under the program's checks.
  ageLabel: "Model year",
  ageFailed: (year) => `${year} is too old`,
  odometerLabel: (miles) => `Under ${miles.toLocaleString("en-US")} miles`,
  vinLabel: "Full VIN",
  vinVerifiedLabel: "VIN verified at pickup",
  notMet: "Not met",
  roMissing: "Missing",
  assumed: "Assumed",
  unknownType: "Not checked",
  locationLabel: LOCATION_CHECKS_LABEL,
  locationDetail: "After delivery",
  later: {
    [RULE_TIMING.AT_PICKUP]: "At pickup",
    [RULE_TIMING.AT_DELIVERY]: "At delivery",
    [RULE_TIMING.MANUAL]: "By delivery"
  },
  laterDefault: "Later"
};
var PROGRAM_CHECKS = [
  {
    key: "ownership_period",
    state: "program",
    label: "Ownership period",
    detail: null,
    info: "First owner: within 3 years of the date of sale. A later owner: within their first year of ownership, while the vehicle is 3 years old or less."
  },
  { key: "ro_qualifying_repair", state: "program", label: "RO is a qualifying repair", detail: null }
];
var PROGRAM_CHECKED_RULE_KEYS = /* @__PURE__ */ new Set(["first_year_owner"]);
var LOCATION_FIELDS = /* @__PURE__ */ new Set([
  "consumerPickupPingNearby",
  "moveDurationWithinBounds",
  "pickupLocationWithinBounds",
  "deliveryLocationWithinBounds"
]);
var LOCATION_ROW_KEY = "location_and_timing";
var makesOf = (rule) => (rule.makes ?? []).filter((make) => typeof make === "string" && make).join(" or ");
function eligibilityRowLabel(rule) {
  switch (rule.type) {
    case ELIGIBILITY_RULE_TYPES.MAKE_IN:
      return makesOf(rule) ? ELIGIBILITY_ROW_COPY.makeLabel(makesOf(rule)) : eligibilityRuleLabel(rule.key);
    case ELIGIBILITY_RULE_TYPES.MODEL_YEAR_MAX_AGE:
      return ELIGIBILITY_ROW_COPY.ageLabel;
    case ELIGIBILITY_RULE_TYPES.ODOMETER_MAX:
      return rule.maxOdometer != null ? ELIGIBILITY_ROW_COPY.odometerLabel(rule.maxOdometer) : eligibilityRuleLabel(rule.key);
    case ELIGIBILITY_RULE_TYPES.VIN_COMPLETE:
      return ELIGIBILITY_ROW_COPY.vinLabel;
    case ELIGIBILITY_RULE_TYPES.VIN_VERIFIED:
      return ELIGIBILITY_ROW_COPY.vinVerifiedLabel;
    default:
      return eligibilityRuleLabel(rule.key);
  }
}
var laterDetail = (timing) => (typeof timing === "string" ? ELIGIBILITY_ROW_COPY.later[timing] : void 0) ?? ELIGIBILITY_ROW_COPY.laterDefault;

// src/utils/claimEligibility.ts
import {
  ELIGIBILITY_RULE_TYPES as ELIGIBILITY_RULE_TYPES2,
  NOT_EVALUATED_SOURCE as NOT_EVALUATED_SOURCE2,
  ODOMETER_SOURCE,
  PICKUP_VIN_SOURCE,
  RULE_OUTCOMES as RULE_OUTCOMES3,
  RULE_POLICY,
  VIN_DECODED_SOURCE
} from "@hopdrive/sdk-ar";
var CLAIM_ELIGIBILITY_COPY = {
  vinIncomplete: "Incomplete",
  vinMissing: "Missing",
  // The claim keeps the make and year the dealer typed, not the decode the rule read, so a rule
  // judged on the VIN says so rather than quote a typed value it never looked at.
  fromVin: "From VIN",
  makeFromVinFailed: "VIN is a different make",
  ageFromVinFailed: "VIN model year is too old",
  vinVerified: "Matches",
  vinOverridden: "Overridden at pickup",
  vinMismatch: "Different vehicle at pickup",
  noPickupVin: "No VIN at pickup",
  noChecks: "No eligibility checks are recorded for this claim yet."
};
var rulesOf = (eligibility) => {
  const rules = eligibility?.rules;
  return Array.isArray(rules) ? rules.filter((rule) => typeof rule?.key === "string" && typeof rule?.type === "string") : [];
};
function eligibilityRulesByContract(contracts) {
  const byContract = /* @__PURE__ */ new Map();
  for (const contract of contracts ?? []) {
    const definitions = /* @__PURE__ */ new Map();
    const sets = [contract.eligibility, ...(contract.contract_rules ?? []).map((rule) => rule.eligibility)];
    for (const rule of sets.flatMap(rulesOf)) definitions.set(rule.key, [...definitions.get(rule.key) ?? [], rule]);
    const agreed = /* @__PURE__ */ new Map();
    for (const [key, rules] of definitions) {
      const shapes = new Set(rules.map((rule) => JSON.stringify(rule)));
      if (shapes.size === 1) agreed.set(key, rules[0]);
    }
    byContract.set(Number(contract.id), agreed);
  }
  return byContract;
}
var readVerdicts2 = (evaluationResult) => Array.isArray(evaluationResult) ? evaluationResult.flatMap(
  (entry) => typeof entry?.ruleKey === "string" && entry.ruleKey ? [{ key: entry.ruleKey, outcome: entry.outcome, source: entry.source, timing: entry.timing }] : []
) : [];
var CATALOG_RULE_TYPES = {
  qualifying_make: ELIGIBILITY_RULE_TYPES2.MAKE_IN,
  model_year_age: ELIGIBILITY_RULE_TYPES2.MODEL_YEAR_MAX_AGE,
  odometer_limit: ELIGIBILITY_RULE_TYPES2.ODOMETER_MAX,
  full_vin: ELIGIBILITY_RULE_TYPES2.VIN_COMPLETE,
  vin_verified: ELIGIBILITY_RULE_TYPES2.VIN_VERIFIED,
  ro_number: ELIGIBILITY_RULE_TYPES2.FIELD_PRESENT
};
var SOURCE_RULE_TYPES = {
  vehicle_make: ELIGIBILITY_RULE_TYPES2.MAKE_IN,
  vehicle_make_missing: ELIGIBILITY_RULE_TYPES2.MAKE_IN,
  [VIN_DECODED_SOURCE.MAKE]: ELIGIBILITY_RULE_TYPES2.MAKE_IN,
  vehicle_year: ELIGIBILITY_RULE_TYPES2.MODEL_YEAR_MAX_AGE,
  vehicle_year_missing: ELIGIBILITY_RULE_TYPES2.MODEL_YEAR_MAX_AGE,
  [VIN_DECODED_SOURCE.YEAR]: ELIGIBILITY_RULE_TYPES2.MODEL_YEAR_MAX_AGE,
  odometer_unavailable: ELIGIBILITY_RULE_TYPES2.ODOMETER_MAX,
  vehicle_odometer: ELIGIBILITY_RULE_TYPES2.ODOMETER_MAX,
  [ODOMETER_SOURCE.PICKUP_INSPECTION]: ELIGIBILITY_RULE_TYPES2.ODOMETER_MAX,
  [ODOMETER_SOURCE.DELIVERY_INSPECTION]: ELIGIBILITY_RULE_TYPES2.ODOMETER_MAX,
  vehicle_vin: ELIGIBILITY_RULE_TYPES2.VIN_COMPLETE,
  pickup_scanned_vin: ELIGIBILITY_RULE_TYPES2.VIN_COMPLETE,
  vin_incomplete: ELIGIBILITY_RULE_TYPES2.VIN_COMPLETE,
  [PICKUP_VIN_SOURCE.VERIFIED]: ELIGIBILITY_RULE_TYPES2.VIN_VERIFIED,
  [PICKUP_VIN_SOURCE.MISSING]: ELIGIBILITY_RULE_TYPES2.VIN_VERIFIED,
  [PICKUP_VIN_SOURCE.OVERRIDE]: ELIGIBILITY_RULE_TYPES2.VIN_VERIFIED,
  [PICKUP_VIN_SOURCE.MISMATCH]: ELIGIBILITY_RULE_TYPES2.VIN_VERIFIED,
  referenceNum: ELIGIBILITY_RULE_TYPES2.FIELD_PRESENT
};
var ruleFor = (verdict, rules) => {
  const declared = rules?.get(verdict.key);
  if (declared) return declared;
  const type = CATALOG_RULE_TYPES[verdict.key] ?? (typeof verdict.source === "string" ? SOURCE_RULE_TYPES[verdict.source] : void 0);
  return { key: verdict.key, type: type ?? "" };
};
var isLocationRule = (verdict, rule) => LOCATION_RULE_KEYS.has(verdict.key) || rule.field != null && LOCATION_FIELDS.has(rule.field);
var FULL_VIN_LENGTH = 17;
var odometerOf = (move, source) => formatOdometer(source === ODOMETER_SOURCE.DELIVERY_INSPECTION ? move?.delivery_odometer : move?.pickup_odometer);
var text = (value) => value === null || value === void 0 || value === "" ? null : String(value).trim() || null;
var VIN_VERIFIED_FAILURES = {
  [PICKUP_VIN_SOURCE.OVERRIDE]: CLAIM_ELIGIBILITY_COPY.vinOverridden,
  [PICKUP_VIN_SOURCE.MISMATCH]: CLAIM_ELIGIBILITY_COPY.vinMismatch,
  [PICKUP_VIN_SOURCE.MISSING]: CLAIM_ELIGIBILITY_COPY.noPickupVin
};
function passedDetail(rule, verdict, move) {
  switch (rule.type) {
    case ELIGIBILITY_RULE_TYPES2.MAKE_IN:
      return verdict.source === VIN_DECODED_SOURCE.MAKE ? CLAIM_ELIGIBILITY_COPY.fromVin : text(move?.vehicle_make);
    case ELIGIBILITY_RULE_TYPES2.ODOMETER_MAX:
      return odometerOf(move, verdict.source);
    case ELIGIBILITY_RULE_TYPES2.VIN_COMPLETE: {
      const vin = text(move?.vehicle_vin);
      return vin && vin.length === FULL_VIN_LENGTH ? vin : null;
    }
    case ELIGIBILITY_RULE_TYPES2.VIN_VERIFIED:
      return CLAIM_ELIGIBILITY_COPY.vinVerified;
    case ELIGIBILITY_RULE_TYPES2.FIELD_PRESENT:
      return rule.field === "referenceNum" || verdict.source === "referenceNum" || verdict.key === "ro_number" ? text(move?.reference_num) : null;
    default:
      return null;
  }
}
function failedDetail(rule, verdict, move) {
  switch (rule.type) {
    case ELIGIBILITY_RULE_TYPES2.MAKE_IN: {
      if (verdict.source === VIN_DECODED_SOURCE.MAKE) return CLAIM_ELIGIBILITY_COPY.makeFromVinFailed;
      const make = text(move?.vehicle_make);
      return make ? ELIGIBILITY_ROW_COPY.makeFailed(make) : ELIGIBILITY_ROW_COPY.notMet;
    }
    case ELIGIBILITY_RULE_TYPES2.MODEL_YEAR_MAX_AGE: {
      if (verdict.source === VIN_DECODED_SOURCE.YEAR) return CLAIM_ELIGIBILITY_COPY.ageFromVinFailed;
      const year = text(move?.vehicle_year);
      return year ? ELIGIBILITY_ROW_COPY.ageFailed(year) : ELIGIBILITY_ROW_COPY.notMet;
    }
    case ELIGIBILITY_RULE_TYPES2.ODOMETER_MAX:
      return odometerOf(move, verdict.source) ?? ELIGIBILITY_ROW_COPY.notMet;
    case ELIGIBILITY_RULE_TYPES2.VIN_COMPLETE:
      return text(move?.vehicle_vin) ? CLAIM_ELIGIBILITY_COPY.vinIncomplete : CLAIM_ELIGIBILITY_COPY.vinMissing;
    case ELIGIBILITY_RULE_TYPES2.VIN_VERIFIED:
      return typeof verdict.source === "string" && VIN_VERIFIED_FAILURES[verdict.source] || ELIGIBILITY_ROW_COPY.notMet;
    case ELIGIBILITY_RULE_TYPES2.FIELD_PRESENT:
      return ELIGIBILITY_ROW_COPY.roMissing;
    default:
      return ELIGIBILITY_ROW_COPY.notMet;
  }
}
var locationState = (verdicts) => {
  if (verdicts.some((verdict) => verdict.outcome === RULE_OUTCOMES3.FAIL)) return "failed";
  if (verdicts.every((verdict) => verdict.outcome === RULE_OUTCOMES3.PASS)) return "passed";
  return "later";
};
var LOCATION_DETAIL = {
  failed: ELIGIBILITY_ROW_COPY.notMet,
  passed: null,
  later: ELIGIBILITY_ROW_COPY.locationDetail,
  waiting: null,
  assumed: null,
  program: null
};
function claimEligibilityRows(claim, rules) {
  const verdicts = readVerdicts2(claim.evaluation_result);
  const move = claim.move;
  const locationVerdicts = verdicts.filter((verdict) => isLocationRule(verdict, ruleFor(verdict, rules)));
  const rows = [];
  const seen = /* @__PURE__ */ new Set();
  for (const verdict of verdicts) {
    if (seen.has(verdict.key)) continue;
    seen.add(verdict.key);
    const rule = ruleFor(verdict, rules);
    if (isLocationRule(verdict, rule)) {
      if (!rows.some((row2) => row2.key === LOCATION_ROW_KEY)) {
        const state = locationState(locationVerdicts);
        rows.push({ key: LOCATION_ROW_KEY, state, label: ELIGIBILITY_ROW_COPY.locationLabel, detail: LOCATION_DETAIL[state] });
      }
      continue;
    }
    if (PROGRAM_CHECKED_RULE_KEYS.has(verdict.key)) continue;
    const failed = verdict.outcome === RULE_OUTCOMES3.FAIL;
    if (rule.type === ELIGIBILITY_RULE_TYPES2.MODEL_YEAR_MAX_AGE && !failed) continue;
    const row = (state, detail) => rows.push({ key: verdict.key, state, label: eligibilityRowLabel(rule), detail });
    if (verdict.source === NOT_EVALUATED_SOURCE2 || rule.policy === RULE_POLICY.ALWAYS_ASSUME) row("assumed", ELIGIBILITY_ROW_COPY.assumed);
    else if (verdict.outcome === RULE_OUTCOMES3.PASS) row("passed", passedDetail(rule, verdict, move));
    else if (failed) row("failed", failedDetail(rule, verdict, move));
    else if (verdict.outcome === RULE_OUTCOMES3.ASSUME) row("later", laterDetail(verdict.timing));
    else row("assumed", ELIGIBILITY_ROW_COPY.unknownType);
  }
  return rows;
}

// src/ui/ProgramClaimsLedger.tsx
import { useCallback as useCallback3, useMemo as useMemo4, useRef as useRef3, useState as useState6 } from "react";
import { Alert as Alert2, Box as Box8, Button as Button3, Card as Card2, LinearProgress as LinearProgress2, TablePagination, Typography as Typography8 } from "@mui/material";
import { BiReceipt } from "react-icons/bi";
import { allTokens as allTokens5 } from "@hopdrive/ui/tokens";

// src/queries/claimsAggregates.ts
import { LANE_TOLL_ACCESSORIAL_ID as LANE_TOLL_ACCESSORIAL_ID2, TOLL_ACCESSORIAL_CODE as TOLL_ACCESSORIAL_CODE2 } from "@hopdrive/sdk-ar";
var CLAIMS_WHERE_TYPE = "program_claims_bool_exp!";
var queryDocument = (name, variables, fields) => `query ${name}(${Object.entries(variables).map(([variable, type]) => `$${variable}: ${type}`).join(", ")}) {
${fields.map((field) => `  ${field}`).join("\n")}
}`;
var count = (result) => result?.aggregate?.count ?? 0;
var ledgerOf = (claims, ...conditions) => `{_and: [{claim: ${claims}}${conditions.map((condition) => `, ${condition}`).join("")}]}`;
var typeIs = (type) => `{type: {_eq: "${type}"}}`;
var HAS_ACTUAL = "{actual_amount: {_is_null: false}}";
var NO_ACTUAL = "{actual_amount: {_is_null: true}}";
var AMOUNT_LEGS = { paid_back: LEDGER_TYPE.RECOUP, reimbursed: LEDGER_TYPE.REIMBURSEMENT };
var ledgerSumFields = (prefix, claims) => [
  `${prefix}covered: program_ledger_aggregate(where: ${ledgerOf(claims, typeIs(LEDGER_TYPE.PROGRAM_PAYMENT))}) { aggregate { count sum { program_max } } }`,
  ...Object.entries(AMOUNT_LEGS).flatMap(([leg, type]) => [
    `${prefix}${leg}_actual: program_ledger_aggregate(where: ${ledgerOf(claims, typeIs(type), HAS_ACTUAL)}) { aggregate { count sum { actual_amount } } }`,
    `${prefix}${leg}_estimated: program_ledger_aggregate(where: ${ledgerOf(claims, typeIs(type), NO_ACTUAL)}) { aggregate { count sum { estimated_amount } } }`
  ]),
  // Whether any row is still an estimate needs only one such row, not a count of them all.
  `${prefix}unconfirmed: program_ledger(where: ${ledgerOf(claims, "{confirmed: {_eq: false}}")}, limit: 1) { id }`
];
var legSum = (parts) => {
  let rows = 0;
  let total = 0;
  for (const [result, field] of parts) {
    const aggregate = result?.aggregate;
    rows += aggregate?.count ?? 0;
    total += toAmount(aggregate?.sum?.[field]);
  }
  return rows === 0 ? null : total;
};
var readLedgerSums = (data, prefix = "") => {
  const amountLeg = (leg) => legSum([
    [data[`${prefix}${leg}_actual`], "actual_amount"],
    [data[`${prefix}${leg}_estimated`], "estimated_amount"]
  ]);
  const unconfirmed = data[`${prefix}unconfirmed`];
  return {
    covered: legSum([[data[`${prefix}covered`], "program_max"]]),
    paidBack: amountLeg("paid_back"),
    reimbursed: amountLeg("reimbursed"),
    estimated: Array.isArray(unconfirmed) && unconfirmed.length > 0
  };
};
var ELIGIBILITY_STATUSES = Object.values(ELIGIBILITY_STATUS);
var claimsSummaryQuery = (where, tilesWhere) => ({
  document: queryDocument("ProgramClaimsSummary", { where: CLAIMS_WHERE_TYPE, tiles: CLAIMS_WHERE_TYPE }, [
    "claims: program_claims_aggregate(where: $where) { aggregate { count } }",
    ...ledgerSumFields("", "$where"),
    "tiles: program_claims_aggregate(where: $tiles) { aggregate { count } }",
    ...ELIGIBILITY_STATUSES.map(
      (status) => `tile_${status}: program_claims_aggregate(where: {_and: [$tiles, {eligibility_status: {_eq: "${status}"}}]}) { aggregate { count } }`
    )
  ]),
  variables: { where, tiles: tilesWhere }
});
var readClaimsSummary = (data) => ({
  count: count(data.claims),
  sums: readLedgerSums(data),
  beforeEligibility: {
    count: count(data.tiles),
    byStatus: Object.fromEntries(ELIGIBILITY_STATUSES.map((status) => [status, count(data[`tile_${status}`])]))
  }
});
var fetchClaimsSummary = async (request, where, tilesWhere) => {
  const { document, variables } = claimsSummaryQuery(where, tilesWhere);
  return readClaimsSummary(await request(document, variables) ?? {});
};
var STRAY_PROBE_LIMIT = 200;
var rooftopCountsQuery = (where, rooftops, known) => {
  const variables = { where, known };
  const types = { where: CLAIMS_WHERE_TYPE, known: "[bigint!]!" };
  const fields = rooftops.map(({ rooftopId }, index) => {
    variables[`r${index}`] = rooftopWhere(rooftopId);
    types[`r${index}`] = CLAIMS_WHERE_TYPE;
    return `r${index}: program_claims_aggregate(where: {_and: [$where, $r${index}]}) { aggregate { count } }`;
  });
  fields.push(
    `strays: program_claims(where: {_and: [$where, {move: {customer_id: {_nin: $known}}}]}, limit: ${STRAY_PROBE_LIMIT}) { id move { id customer { id name } } }`
  );
  return { document: queryDocument("ProgramClaimsRooftops", types, fields), variables };
};
var fetchRooftopCounts = async (request, where, candidates) => {
  const names = new Map(candidates.map((candidate) => [candidate.id, candidate.name || String(candidate.id)]));
  names.delete(UNKNOWN_ROOFTOP_ID);
  let pending = [UNKNOWN_ROOFTOP_ID, ...names.keys()];
  const counts = [];
  while (pending.length) {
    const rooftops = pending.map((rooftopId) => ({ rooftopId }));
    const { document, variables } = rooftopCountsQuery(where, rooftops, [...names.keys()]);
    const data = await request(document, variables) ?? {};
    pending.forEach((rooftopId, index) => {
      const claims = count(data[`r${index}`]);
      if (claims > 0) {
        counts.push({ rooftopId, rooftopName: rooftopId === UNKNOWN_ROOFTOP_ID ? UNKNOWN_ROOFTOP_NAME : names.get(rooftopId), count: claims });
      }
    });
    pending = [];
    for (const stray of data.strays ?? []) {
      const customer = stray.move?.customer;
      if (customer && !names.has(customer.id)) {
        names.set(customer.id, customer.name || String(customer.id));
        pending.push(customer.id);
      }
    }
  }
  return counts.sort(byRooftopName);
};
var rooftopSumsQuery = (where, rooftops) => {
  const variables = { where };
  const types = { where: CLAIMS_WHERE_TYPE };
  const fields = rooftops.flatMap(({ rooftopId }, index) => {
    variables[`r${index}`] = rooftopWhere(rooftopId);
    types[`r${index}`] = CLAIMS_WHERE_TYPE;
    return ledgerSumFields(`r${index}_`, `{_and: [$where, $r${index}]}`);
  });
  return { document: queryDocument("ProgramClaimsRooftopSums", types, fields), variables };
};
var fetchRooftopSummaries = async (request, where, rooftops) => {
  if (!rooftops.length) return [];
  const { document, variables } = rooftopSumsQuery(where, rooftops);
  const data = await request(document, variables) ?? {};
  return rooftops.map((rooftop, index) => ({ ...rooftop, sums: readLedgerSums(data, `r${index}_`) }));
};
var ACCESSORIAL_ROW = `{source_type: {_eq: "${LEDGER_SOURCE_TYPE.ACCESSORIAL}"}}`;
var fetchSourceOptions = async (request, where) => {
  const document = queryDocument("ProgramClaimsSources", { where: CLAIMS_WHERE_TYPE }, [
    `legs: program_ledger(where: ${ledgerOf("$where", ACCESSORIAL_ROW)}, distinct_on: move_id) { id move_id move { id accessorials { id code } } }`,
    `lane_tolls: program_ledger(where: ${ledgerOf("$where", ACCESSORIAL_ROW, `{source_id: {_eq: "${LANE_TOLL_ACCESSORIAL_ID2}"}}`)}, limit: 1) { id }`
  ]);
  const data = await request(document, { where }) ?? {};
  const sources = /* @__PURE__ */ new Set();
  for (const leg of data.legs ?? []) {
    for (const accessorial of leg.move?.accessorials ?? []) if (accessorial.code) sources.add(accessorialName(accessorial.code));
  }
  if (Array.isArray(data.lane_tolls) && data.lane_tolls.length > 0) sources.add(accessorialName(TOLL_ACCESSORIAL_CODE2));
  return [...sources].sort((a, b) => a.localeCompare(b));
};
var COLUMN_TYPES = {
  [MONEY_COLUMN.COVERED]: [LEDGER_TYPE.PROGRAM_PAYMENT],
  [MONEY_COLUMN.PAID_BACK]: [LEDGER_TYPE.RECOUP],
  [MONEY_COLUMN.REIMBURSED]: [LEDGER_TYPE.REIMBURSEMENT],
  [MONEY_COLUMN.TOTAL_MOVE_COST]: [LEDGER_TYPE.PROGRAM_PAYMENT, LEDGER_TYPE.RECOUP]
};
var fetchUnusedColumns = async (request, scope, columns) => {
  if (!columns.length) return [];
  const types = [...new Set(columns.flatMap((column2) => COLUMN_TYPES[column2]))];
  const document = queryDocument(
    "ProgramClaimsColumnsUsed",
    { scope: CLAIMS_WHERE_TYPE },
    types.map((type) => `${type}: program_ledger(where: ${ledgerOf("$scope", typeIs(type))}, limit: 1) { id }`)
  );
  const data = await request(document, { scope }) ?? {};
  const booked = (type) => Array.isArray(data[type]) && data[type].length > 0;
  return columns.filter((column2) => !COLUMN_TYPES[column2].some(booked));
};

// src/utils/activeFilters.ts
var ROOFTOP = "rooftop";
function buildActiveFilters(values, customers = []) {
  const filters = [];
  const { eligibility, submission, failedRule, customerIds, sources, datePreset, view } = values;
  const search = values.search.trim();
  if (eligibility) {
    const label = view.eligibilityOptions.find((option2) => option2.value === eligibility)?.label ?? eligibility;
    filters.push({ key: CLAIMS_FILTER.ELIGIBILITY, label: `Eligibility: ${label}` });
  }
  if (submission) {
    const label = submission === ANY_SUBMITTED ? ANY_SUBMITTED_LABEL : SUBMISSION_LABELS[submission] ?? submission;
    filters.push({ key: CLAIMS_FILTER.SUBMISSION, label: `Submission: ${label}` });
  }
  if (failedRule) filters.push({ key: CLAIMS_FILTER.FAILED_RULE, label: `Failed rule: ${eligibilityRuleLabel(failedRule)}` });
  if (customerIds.length === 1) {
    const name = customers.find((customer) => customer.id === customerIds[0])?.name;
    filters.push({ key: CLAIMS_FILTER.CUSTOMERS, label: name ?? formatCountOf(1, ROOFTOP) });
  } else if (customerIds.length > 1) {
    filters.push({ key: CLAIMS_FILTER.CUSTOMERS, label: formatCountOf(customerIds.length, ROOFTOP) });
  }
  if (sources.length) filters.push({ key: CLAIMS_FILTER.SOURCES, label: `Source: ${sources.join(", ")}` });
  if (datePreset !== DEFAULT_DATE_PRESET) {
    const label = DATE_PRESET_OPTIONS.find((option2) => option2.value === datePreset)?.label ?? datePreset;
    filters.push({ key: CLAIMS_FILTER.DATE, label });
  }
  if (search) filters.push({ key: CLAIMS_FILTER.SEARCH, label: `"${search}"` });
  return filters;
}

// src/utils/claimFilters.ts
var ALL_CLAIMS_LABEL = "All claims";
var eligibilityTiles = (view, counts) => [
  { value: null, label: ALL_CLAIMS_LABEL, count: counts.count },
  ...view.eligibilityOptions.map((option2) => ({
    value: option2.value,
    label: option2.label,
    count: option2.statuses.reduce((total, status) => total + (counts.byStatus[status] ?? 0), 0)
  }))
];

// src/utils/formatLeg.ts
var formatLeg = (view, total, estimated) => {
  if (total === null) return PLACEHOLDER;
  return `${formatCurrency(total)}${view.marksEstimates && estimated ? ESTIMATE_MARK : ""}`;
};

// src/utils/sortClaims.ts
var CLAIM_SORT_FIELD = {
  PICKUP: "pickup",
  ROOFTOP: "rooftop",
  ELIGIBILITY: "eligibility",
  SUBMISSION: "submission"
};
var SORT_DIRECTION = { ASC: "asc", DESC: "desc" };
var DEFAULT_CLAIM_SORT = { field: CLAIM_SORT_FIELD.PICKUP, direction: SORT_DIRECTION.DESC };
var ORDER_PATH = {
  [CLAIM_SORT_FIELD.PICKUP]: (direction) => ({ move: { pickup_time: direction } }),
  [CLAIM_SORT_FIELD.ROOFTOP]: (direction) => ({ move: { customer: { name: direction } } }),
  // By the stored status, which for staff orders as the labels do. A dealer reads an override
  // as Eligible, yet it sorts after Ineligible, by the status the server holds.
  [CLAIM_SORT_FIELD.ELIGIBILITY]: (direction) => ({ eligibility_status: direction }),
  [CLAIM_SORT_FIELD.SUBMISSION]: (direction) => ({ submission_status: direction })
};
var claimsOrderBy = (sort) => [
  ORDER_PATH[sort.field](`${sort.direction}_nulls_last`),
  { id: sort.direction }
];

// src/useServerValue.ts
import { useEffect as useEffect2, useRef, useState as useState2 } from "react";
function useServerValue(load) {
  const [state, setState] = useState2({ value: void 0, loading: Boolean(load), error: null });
  const latest = useRef(0);
  useEffect2(() => {
    const run = ++latest.current;
    if (!load) {
      setState({ value: void 0, loading: false, error: null });
      return;
    }
    setState((previous) => ({ ...previous, loading: true, error: null }));
    load().then(
      (value) => {
        if (run === latest.current) setState({ value, loading: false, error: null });
      },
      (error) => {
        if (run === latest.current) setState({ value: void 0, loading: false, error });
      }
    );
  }, [load]);
  return state;
}

// src/ui/AllClaimsTable.tsx
import { TableBody as TableBody3, TableHead as TableHead3, TableRow as TableRow3 } from "@mui/material";

// src/ui/cells.tsx
import {
  Table,
  TableCell,
  TableContainer
} from "@mui/material";
import { allTokens as allTokens2 } from "@hopdrive/ui/tokens";

// src/ui/tokens.ts
import { allTokens } from "@hopdrive/ui/tokens";
var { palette, typography } = allTokens;
var GREY = palette.grey;
var DIVIDER = allTokens.divider.light;
var TONE = {
  NEUTRAL: { bg: palette.grey[100], color: palette.grey[700] },
  SUCCESS: { bg: palette.success[50], color: palette.success[700] },
  INFO: { bg: palette.info[50], color: palette.info[700] },
  WARNING: { bg: palette.warning[50], color: palette.warning[700] },
  ERROR: { bg: palette.error[50], color: palette.error[700] }
};
var ID_COLOR = palette.secondaryAccent.main;
var elevationShadow = (level) => level.layers.map((layer) => `${layer.offsetX}px ${layer.offsetY}px ${layer.blur}px ${layer.spread}px rgba(0,0,0,${layer.opacity})`).join(", ");
var SANS_FALLBACK = "sans-serif";
var MONO_FALLBACK = "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";
var quoted = (family) => `"${family}"`;
var FONT_FAMILY = {
  HEADING: [quoted(typography.fontFamily.heading), quoted(typography.fontFamily.body), SANS_FALLBACK].join(", "),
  BODY: [quoted(typography.fontFamily.body), SANS_FALLBACK].join(", "),
  MONO: [quoted(typography.fontFamily.mono), MONO_FALLBACK].join(", ")
};

// src/ui/cells.tsx
import { jsx } from "react/jsx-runtime";
var FONT_SIZE = { CAPTION: 12, SMALL: 13, BODY: 14 };
var withSx = (preset, sx) => [preset, ...Array.isArray(sx) ? sx : sx ? [sx] : []];
var headerSx = {
  fontFamily: FONT_FAMILY.HEADING,
  fontSize: FONT_SIZE.CAPTION,
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  whiteSpace: "nowrap",
  color: GREY[600],
  bgcolor: GREY[100],
  px: 1.5,
  py: 1.25
};
var numericHeaderSx = { ...headerSx, textAlign: "right" };
var nestedHeaderSx = { ...headerSx, bgcolor: "transparent" };
var nestedNumericHeaderSx = { ...nestedHeaderSx, textAlign: "right" };
var bodySx = { fontFamily: FONT_FAMILY.BODY, fontSize: FONT_SIZE.BODY, px: 1.5, py: 1 };
var numericBodySx = { ...bodySx, textAlign: "right", fontWeight: 600, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" };
var idSx = { fontFamily: FONT_FAMILY.MONO, fontSize: FONT_SIZE.SMALL, color: ID_COLOR, px: 1.5, py: 1 };
var TableFrame = ({ sx, ...props }) => /* @__PURE__ */ jsx(
  TableContainer,
  {
    ...props,
    sx: withSx({ borderRadius: `${allTokens2.radius.md}px`, border: `1px solid ${DIVIDER}`, bgcolor: "background.paper" }, sx)
  }
);
var ClaimsTable = ({ sx, ...props }) => /* @__PURE__ */ jsx(Table, { ...props, sx: withSx({ "& .MuiTableCell-root": { borderBottom: `1px solid ${DIVIDER}` } }, sx) });
var presetCell = (preset) => {
  const PresetCell = ({ sx, ...props }) => /* @__PURE__ */ jsx(TableCell, { ...props, sx: withSx(preset, sx) });
  return PresetCell;
};
var HeaderCell = presetCell(headerSx);
var NumericHeaderCell = presetCell(numericHeaderSx);
var NestedHeaderCell = presetCell(nestedHeaderSx);
var NestedNumericHeaderCell = presetCell(nestedNumericHeaderSx);
var BodyCell = presetCell(bodySx);
var NumericBodyCell = presetCell(numericBodySx);
var IdCell = presetCell(idSx);

// src/ui/ProgramClaimsTable.tsx
import { Fragment as Fragment2, memo, useCallback as useCallback2, useEffect as useEffect3, useMemo as useMemo3, useRef as useRef2, useState as useState3 } from "react";
import {
  Alert,
  Box as Box4,
  Button,
  Collapse,
  IconButton,
  LinearProgress,
  Table as Table3,
  TableBody as TableBody2,
  TableHead as TableHead2,
  TableRow as TableRow2,
  TableSortLabel,
  Tooltip as Tooltip3,
  Typography as Typography4
} from "@mui/material";
import { BiChevronRight } from "react-icons/bi";

// src/utils/density.ts
var DENSITY = {
  WIDE: "wide",
  MEDIUM: "medium",
  NARROW: "narrow",
  COMPACT: "compact"
};
var densityForWidth = (width, needs) => {
  if (width === null || width >= needs.wide) return DENSITY.WIDE;
  if (width >= needs.medium) return DENSITY.MEDIUM;
  if (needs.narrow === void 0 || width >= needs.narrow) return DENSITY.NARROW;
  return DENSITY.COMPACT;
};

// src/utils/claimColumns.ts
var CLAIM_COLUMN = {
  TOGGLE: "toggle",
  CLAIM: "claim",
  MOVE: "move",
  ROOFTOP: "rooftop",
  DETAILS: "details",
  ELIGIBILITY: "eligibility",
  SUBMISSION: "submission",
  DISTANCE: "distance",
  STATUS: "status",
  AMOUNTS: "amounts",
  PICKUP: "pickup",
  ACTIONS: "actions"
};
var column = (kind, label, extra = {}) => ({ kind, key: kind, label, ...extra });
function claimColumnsFor({ view, density, showClaimId, showRooftop, hasActions }) {
  if (density === DENSITY.COMPACT) {
    const compact = [column(CLAIM_COLUMN.TOGGLE, ""), column(CLAIM_COLUMN.DETAILS, "Claim", { sortField: CLAIM_SORT_FIELD.PICKUP })];
    return hasActions ? [...compact, column(CLAIM_COLUMN.ACTIONS, "")] : compact;
  }
  const narrow = density === DENSITY.NARROW;
  const wide = density === DENSITY.WIDE;
  const columns = [column(CLAIM_COLUMN.TOGGLE, "")];
  if (showClaimId && !narrow) columns.push(column(CLAIM_COLUMN.CLAIM, "Claim"));
  columns.push(column(CLAIM_COLUMN.MOVE, "Move"));
  if (showRooftop && !narrow) columns.push(column(CLAIM_COLUMN.ROOFTOP, "Rooftop", { sortField: CLAIM_SORT_FIELD.ROOFTOP }));
  columns.push(column(CLAIM_COLUMN.DETAILS, narrow ? "Claim" : "RO / Vehicle", narrow ? { sortField: CLAIM_SORT_FIELD.PICKUP } : {}));
  if (narrow) {
    columns.push(column(CLAIM_COLUMN.STATUS, "Status", { sortField: CLAIM_SORT_FIELD.ELIGIBILITY }));
  } else {
    columns.push(column(CLAIM_COLUMN.ELIGIBILITY, "Eligibility", { sortField: CLAIM_SORT_FIELD.ELIGIBILITY }));
    columns.push(column(CLAIM_COLUMN.SUBMISSION, "Submission", { sortField: CLAIM_SORT_FIELD.SUBMISSION }));
    columns.push(column(CLAIM_COLUMN.DISTANCE, "Distance", { numeric: true }));
  }
  if (wide) {
    for (const money of view.columns) {
      columns.push({ kind: "money", key: money.key, label: money.label, numeric: true, money });
    }
  } else {
    columns.push(column(CLAIM_COLUMN.AMOUNTS, "Amounts", { numeric: true }));
  }
  if (!narrow) columns.push(column(CLAIM_COLUMN.PICKUP, "Date", { sortField: CLAIM_SORT_FIELD.PICKUP }));
  if (hasActions) columns.push(column(CLAIM_COLUMN.ACTIONS, ""));
  return columns;
}
var MIN_WIDTH = {
  [CLAIM_COLUMN.TOGGLE]: 48,
  [CLAIM_COLUMN.CLAIM]: 72,
  [CLAIM_COLUMN.MOVE]: 88,
  [CLAIM_COLUMN.ROOFTOP]: 150,
  [CLAIM_COLUMN.DETAILS]: 170,
  [CLAIM_COLUMN.ELIGIBILITY]: 150,
  [CLAIM_COLUMN.SUBMISSION]: 130,
  [CLAIM_COLUMN.DISTANCE]: 90,
  [CLAIM_COLUMN.STATUS]: 150,
  [CLAIM_COLUMN.AMOUNTS]: 190,
  [CLAIM_COLUMN.PICKUP]: 110,
  [CLAIM_COLUMN.ACTIONS]: 56,
  money: 140
};
var claimTableWidth = (options) => claimColumnsFor(options).reduce((total, column2) => total + MIN_WIDTH[column2.kind], 0);
var claimTableDensity = (width, options) => densityForWidth(width, {
  wide: claimTableWidth({ ...options, density: DENSITY.WIDE }),
  medium: claimTableWidth({ ...options, density: DENSITY.MEDIUM }),
  narrow: claimTableWidth({ ...options, density: DENSITY.NARROW })
});

// src/ui/chips.tsx
import { Chip } from "@mui/material";
import { jsx as jsx2 } from "react/jsx-runtime";
var CHIP = { HEIGHT: 22, FONT_SIZE: 11, FONT_WEIGHT: 600 };
var { NEUTRAL, SUCCESS, INFO, ERROR } = TONE;
var ELIGIBILITY_TONE = {
  [ELIGIBILITY_STATUS.ELIGIBLE]: SUCCESS,
  [ELIGIBILITY_STATUS.OVERRIDE_ELIGIBLE]: INFO,
  [ELIGIBILITY_STATUS.INELIGIBLE]: ERROR
};
var SUBMISSION_TONE = {
  [SUBMISSION_STATUS.NOT_SUBMITTED]: NEUTRAL,
  [SUBMISSION_STATUS.SUBMITTED]: INFO,
  [SUBMISSION_STATUS.ACCEPTED]: SUCCESS,
  [SUBMISSION_STATUS.PAID]: SUCCESS,
  [SUBMISSION_STATUS.REJECTED]: ERROR
};
var StatusChip = ({ label, tone }) => /* @__PURE__ */ jsx2(Chip, { label, size: "small", sx: { height: CHIP.HEIGHT, fontSize: CHIP.FONT_SIZE, fontWeight: CHIP.FONT_WEIGHT, bgcolor: tone.bg, color: tone.color } });
var isEstimatedClaim = (submissionStatus) => submissionStatus == null || submissionStatus === SUBMISSION_STATUS.NOT_SUBMITTED;
var EligibilityChip = ({
  view,
  status,
  estimated
}) => {
  const label = eligibilityLabel(view, status) || PLACEHOLDER;
  const option2 = view.eligibilityOptions.find((candidate) => status && candidate.statuses.includes(status));
  const tone = ELIGIBILITY_TONE[option2?.value ?? status ?? ""] ?? NEUTRAL;
  return /* @__PURE__ */ jsx2(StatusChip, { label: estimated ? `${label}${ESTIMATE_MARK}` : label, tone });
};
var SubmissionChip = ({ status }) => {
  const key = status ?? "";
  return /* @__PURE__ */ jsx2(StatusChip, { label: SUBMISSION_LABELS[key] ?? (key || PLACEHOLDER), tone: SUBMISSION_TONE[key] ?? NEUTRAL });
};
var LedgerStateChip = ({ confirmed }) => /* @__PURE__ */ jsx2(StatusChip, { label: confirmed ? "Confirmed" : "Estimate", tone: confirmed ? SUCCESS : NEUTRAL });

// src/ui/ClaimEligibility.tsx
import { useMemo as useMemo2 } from "react";
import { Box as Box2, Typography as Typography2 } from "@mui/material";

// src/ui/EligibilityRuleRows.tsx
import { Box, Tooltip, Typography, useTheme } from "@mui/material";
import { MdCheck, MdClose, MdInfoOutline, MdMoreHoriz, MdOutlineShield, MdRemove, MdSchedule } from "react-icons/md";
import { Fragment, jsx as jsx3, jsxs } from "react/jsx-runtime";
var LINE = { lineHeight: "18px", paddingTop: "2px", paddingBottom: "2px" };
var LINE_BOX = { display: "inline-flex", alignItems: "center", flexShrink: 0, height: 22 };
var RuleRow = ({ row }) => {
  const theme = useTheme();
  const STATE_ICON = {
    passed: { Icon: MdCheck, color: theme?.palette?.success?.main },
    failed: { Icon: MdClose, color: theme?.palette?.error?.main },
    later: { Icon: MdSchedule, color: theme?.palette?.text?.secondary },
    waiting: { Icon: MdMoreHoriz, color: theme?.palette?.text?.disabled },
    assumed: { Icon: MdRemove, color: theme?.palette?.text?.disabled },
    program: { Icon: MdOutlineShield, color: theme?.palette?.text?.disabled }
  };
  const { Icon, color } = STATE_ICON[row.state];
  const failed = row.state === "failed";
  return (
    // Top-aligned so a label that wraps keeps its icon and its detail beside its first line.
    /* @__PURE__ */ jsxs(Box, { "data-state": row.state, sx: { display: "flex", alignItems: "flex-start", gap: "8px", minHeight: 22 }, children: [
      /* @__PURE__ */ jsx3(Box, { component: "span", sx: LINE_BOX, children: /* @__PURE__ */ jsx3(Icon, { size: 16, color }) }),
      /* @__PURE__ */ jsx3(
        Typography,
        {
          "data-testid": "program-eligibility-rule",
          sx: {
            ...LINE,
            fontSize: `13px`,
            color: failed ? theme?.palette?.error?.main : row.state === "program" ? theme?.palette?.text?.secondary : void 0
          },
          children: row.label
        }
      ),
      row.info ? /* @__PURE__ */ jsx3(Tooltip, { title: row.info, placement: "top", arrow: true, children: /* @__PURE__ */ jsx3(Box, { component: "span", "aria-label": row.info, sx: { ...LINE_BOX, cursor: "help" }, children: /* @__PURE__ */ jsx3(MdInfoOutline, { size: 14, color: theme?.palette?.text?.disabled }) }) }) : null,
      row.detail ? /* @__PURE__ */ jsx3(
        Typography,
        {
          "data-testid": "program-eligibility-rule-detail",
          sx: {
            ...LINE,
            marginLeft: "auto",
            paddingLeft: "16px",
            fontSize: `12px`,
            whiteSpace: "nowrap",
            fontWeight: failed ? 500 : void 0,
            color: failed ? theme?.palette?.error?.main : theme?.palette?.text?.secondary
          },
          children: row.detail
        }
      ) : null
    ] })
  );
};
var EligibilityRuleRows = ({ view }) => (
  // Capped so the right-hand detail stays beside its label in a full-width panel.
  /* @__PURE__ */ jsxs(Box, { sx: { display: "flex", flexDirection: "column", gap: "4px", maxWidth: 460 }, children: [
    view.rows.map((row) => /* @__PURE__ */ jsx3(RuleRow, { row }, row.key)),
    view.programChecks.length > 0 ? /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx3(
        Typography,
        {
          sx: { marginTop: "8px", fontSize: `11px`, fontWeight: 600, color: "text.disabled", textTransform: "uppercase", letterSpacing: "0.03em" },
          children: "Checked by the program"
        }
      ),
      view.programChecks.map((row) => /* @__PURE__ */ jsx3(RuleRow, { row }, row.key))
    ] }) : null
  ] })
);
var EligibilityRuleRows_default = EligibilityRuleRows;

// src/ui/ClaimEligibility.tsx
import { jsx as jsx4 } from "react/jsx-runtime";
var ClaimEligibility = ({ claim, rules }) => {
  const rows = useMemo2(() => claimEligibilityRows(claim, rules), [claim, rules]);
  return /* @__PURE__ */ jsx4(Box2, { "data-testid": "claim-eligibility", sx: { px: { xs: 1.5, sm: 3 }, py: 2, bgcolor: GREY[50] }, children: rows.length === 0 ? /* @__PURE__ */ jsx4(Typography2, { sx: { fontSize: FONT_SIZE.SMALL, color: "text.secondary" }, children: CLAIM_ELIGIBILITY_COPY.noChecks }) : /* @__PURE__ */ jsx4(EligibilityRuleRows_default, { view: { rows, programChecks: PROGRAM_CHECKS } }) });
};
var ClaimEligibility_default = ClaimEligibility;

// src/ui/ClaimHoldBadge.tsx
import { Chip as Chip2, Tooltip as Tooltip2 } from "@mui/material";

// src/utils/reviewHold.ts
var REVIEW_HOLD_REASON = {
  SUBMITTED_CLAIM_RERUN_NEEDED: "submitted_claim_rerun_needed"
};
var REVIEW_HOLD_LABEL = "On hold: already filed with the program";
var holdOf = (reviewHold) => reviewHold != null && typeof reviewHold === "object" ? reviewHold : null;
var isClaimHeld = (reviewHold) => holdOf(reviewHold) != null;
var describeReviewHold = (reviewHold) => {
  const status = holdOf(reviewHold)?.submission_status;
  return `Already filed with the program${typeof status === "string" ? ` (${status})` : ""}, and re-evaluating it would change it. HopDrive must settle it with the program before it changes.`;
};

// src/ui/ClaimHoldBadge.tsx
import { jsx as jsx5 } from "react/jsx-runtime";
var BADGE = { HEIGHT: 22, FONT_SIZE: 11, FONT_WEIGHT: 600 };
var ClaimHoldBadge = ({ reviewHold }) => {
  if (!isClaimHeld(reviewHold)) return null;
  return /* @__PURE__ */ jsx5(Tooltip2, { title: describeReviewHold(reviewHold), children: /* @__PURE__ */ jsx5(
    Chip2,
    {
      label: REVIEW_HOLD_LABEL,
      size: "small",
      sx: { height: BADGE.HEIGHT, fontSize: BADGE.FONT_SIZE, fontWeight: BADGE.FONT_WEIGHT, bgcolor: TONE.WARNING.bg, color: TONE.WARNING.color }
    }
  ) });
};
var ClaimHoldBadge_default = ClaimHoldBadge;

// src/ui/LedgerDetail.tsx
import { Box as Box3, Table as Table2, TableBody, TableHead, TableRow, Typography as Typography3 } from "@mui/material";
import { jsx as jsx6, jsxs as jsxs2 } from "react/jsx-runtime";
var EmptyLedger = () => /* @__PURE__ */ jsx6(Typography3, { sx: { fontSize: FONT_SIZE.SMALL, color: "text.secondary" }, children: "No ledger entries for this claim yet." });
var SourceText = ({ children }) => /* @__PURE__ */ jsx6(Typography3, { component: "span", sx: { fontSize: FONT_SIZE.CAPTION, color: "text.secondary" }, children });
var BookedRows = ({ ledger, programName }) => /* @__PURE__ */ jsxs2(Table2, { size: "small", "aria-label": "Ledger entries", children: [
  /* @__PURE__ */ jsx6(TableHead, { children: /* @__PURE__ */ jsxs2(TableRow, { children: [
    /* @__PURE__ */ jsx6(NestedHeaderCell, { children: "Move" }),
    /* @__PURE__ */ jsx6(NestedHeaderCell, { children: "Source" }),
    /* @__PURE__ */ jsx6(NestedHeaderCell, { children: "Detail" }),
    /* @__PURE__ */ jsx6(NestedNumericHeaderCell, { children: "Program max" }),
    /* @__PURE__ */ jsx6(NestedNumericHeaderCell, { children: "Estimated" }),
    /* @__PURE__ */ jsx6(NestedNumericHeaderCell, { children: "Actual" }),
    /* @__PURE__ */ jsx6(NestedHeaderCell, { children: "State" })
  ] }) }),
  /* @__PURE__ */ jsx6(TableBody, { children: ledger.map((entry) => /* @__PURE__ */ jsxs2(TableRow, { children: [
    /* @__PURE__ */ jsx6(IdCell, { children: entry.move_id }),
    /* @__PURE__ */ jsx6(BodyCell, { children: /* @__PURE__ */ jsx6(SourceText, { children: ledgerSourceLabel(entry) }) }),
    /* @__PURE__ */ jsx6(BodyCell, { children: ledgerLegDescription(entry.type, programName) }),
    /* @__PURE__ */ jsx6(NumericBodyCell, { children: formatCurrency(entry.program_max) }),
    /* @__PURE__ */ jsx6(NumericBodyCell, { children: formatCurrency(entry.estimated_amount) }),
    /* @__PURE__ */ jsx6(NumericBodyCell, { children: formatCurrency(entry.actual_amount) }),
    /* @__PURE__ */ jsx6(BodyCell, { children: /* @__PURE__ */ jsx6(LedgerStateChip, { confirmed: entry.confirmed }) })
  ] }, entry.id)) })
] });
var SourceLines = ({ view, ledger }) => /* @__PURE__ */ jsxs2(Table2, { size: "small", "aria-label": "Ledger by source", children: [
  /* @__PURE__ */ jsx6(TableHead, { children: /* @__PURE__ */ jsxs2(TableRow, { children: [
    /* @__PURE__ */ jsx6(NestedHeaderCell, { children: "Move" }),
    /* @__PURE__ */ jsx6(NestedHeaderCell, { children: "Source" }),
    view.columns.map((column2) => /* @__PURE__ */ jsx6(NestedNumericHeaderCell, { children: column2.label }, column2.key))
  ] }) }),
  /* @__PURE__ */ jsx6(TableBody, { children: groupLedgerBySource(ledger).map((source) => /* @__PURE__ */ jsxs2(TableRow, { children: [
    /* @__PURE__ */ jsx6(IdCell, { children: source.moveId }),
    /* @__PURE__ */ jsx6(BodyCell, { children: /* @__PURE__ */ jsx6(SourceText, { children: source.label }) }),
    view.columns.map((column2) => /* @__PURE__ */ jsx6(NumericBodyCell, { children: formatLeg(view, column2.total(source.rows), isLedgerEstimated(source.rows)) }, column2.key))
  ] }, source.key)) })
] });
var LedgerDetail = ({ view, ledger, programName }) => (
  // The ledger's columns are all figures a reader compares, so a narrow card scrolls them sideways
  // rather than dropping any.
  /* @__PURE__ */ jsx6(Box3, { sx: { px: { xs: 1.5, sm: 3 }, py: 2, bgcolor: GREY[50], overflowX: "auto" }, children: ledger.length === 0 ? /* @__PURE__ */ jsx6(EmptyLedger, {}) : view.key === LEDGER_VIEW.HOPDRIVE ? /* @__PURE__ */ jsx6(BookedRows, { ledger, programName }) : /* @__PURE__ */ jsx6(SourceLines, { view, ledger }) })
);
var LedgerDetail_default = LedgerDetail;

// src/ui/ProgramClaimsTable.tsx
import { Fragment as Fragment3, jsx as jsx7, jsxs as jsxs3 } from "react/jsx-runtime";
var TOGGLE_WIDTH = 48;
var TOGGLE_ICON_SIZE = 18;
var TOGGLE_TURN = "transform 150ms ease";
var TOGGLE_OPEN_ROTATION = "rotate(90deg)";
var ACTIONS_WIDTH = 56;
var CAPTION_SEPARATOR = " \xB7 ";
var CLAIMS_SHOWN_STEP = 100;
var ROOFTOP_CLAIMS_ORDER = claimsOrderBy(DEFAULT_CLAIM_SORT);
var rowDensity = (cardWidth, options) => claimTableDensity(cardWidth ?? null, {
  view: options.view,
  showClaimId: options.showClaimId,
  showRooftop: options.showRooftop,
  hasActions: Boolean(options.renderClaimActions)
});
var columnsForRow = ({ view, density = DENSITY.WIDE, showClaimId, showRooftop, renderClaimActions }) => claimColumnsFor({ view, density, showClaimId, showRooftop, hasActions: Boolean(renderClaimActions) });
var useToggleSet = () => {
  const [ids, setIds] = useState3(/* @__PURE__ */ new Set());
  const toggle = (id) => setIds((previous) => {
    const next = new Set(previous);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });
  return [ids, toggle];
};
var Toggle = ({ open, label, onClick }) => /* @__PURE__ */ jsx7(IconButton, { size: "small", "aria-label": `${open ? "Collapse" : "Expand"} ${label}`, "aria-expanded": open, onClick, children: /* @__PURE__ */ jsx7(BiChevronRight, { size: TOGGLE_ICON_SIZE, style: { transition: TOGGLE_TURN, transform: open ? TOGGLE_OPEN_ROTATION : "none" } }) });
var Caption = ({ children, tone = "text.secondary" }) => /* @__PURE__ */ jsx7(Typography4, { sx: { fontSize: FONT_SIZE.CAPTION, color: tone }, children });
var PendingChecks = ({ checks }) => /* @__PURE__ */ jsx7(
  Tooltip3,
  {
    title: /* @__PURE__ */ jsx7(Box4, { component: "span", sx: { display: "flex", flexDirection: "column", gap: 0.25 }, children: checks.map((check) => /* @__PURE__ */ jsx7("span", { children: pendingCheckLine(check) }, check.label)) }),
    children: /* @__PURE__ */ jsxs3(
      Typography4,
      {
        component: "span",
        tabIndex: 0,
        sx: {
          // Its own line under the verdict, underlined only as wide as its words.
          display: "block",
          width: "fit-content",
          fontSize: FONT_SIZE.CAPTION,
          color: TONE.WARNING.color,
          textDecoration: "underline dotted",
          textUnderlineOffset: "2px",
          cursor: "help"
        },
        children: [
          formatCountOf(checks.length, "check"),
          " pending"
        ]
      }
    )
  }
);
var StackedAmounts = ({ view, totals, estimated, align = "end" }) => {
  const booked = totals.filter(({ total }) => total !== null);
  if (!booked.length) return /* @__PURE__ */ jsx7(Fragment3, { children: PLACEHOLDER });
  return /* @__PURE__ */ jsx7(Box4, { sx: { display: "flex", flexDirection: "column", alignItems: align === "end" ? "flex-end" : "flex-start", gap: 0.25 }, children: booked.map(({ column: column2, total }) => /* @__PURE__ */ jsxs3(Box4, { sx: { display: "flex", gap: 1, whiteSpace: "nowrap" }, children: [
    /* @__PURE__ */ jsx7(Typography4, { component: "span", sx: { fontSize: FONT_SIZE.CAPTION, color: GREY[600] }, children: column2.label }),
    /* @__PURE__ */ jsx7(Typography4, { component: "span", sx: { fontSize: FONT_SIZE.SMALL, fontWeight: 600 }, children: formatLeg(view, total, estimated) })
  ] }, column2.key)) });
};
var widthOf = (column2) => column2.kind === CLAIM_COLUMN.TOGGLE ? TOGGLE_WIDTH : column2.kind === CLAIM_COLUMN.ACTIONS ? ACTIONS_WIDTH : void 0;
var ClaimHeaderCells = ({ columns, nested, sort, onSortChange }) => /* @__PURE__ */ jsx7(Fragment3, { children: columns.map((column2) => {
  const Cell = column2.numeric ? nested ? NestedNumericHeaderCell : NumericHeaderCell : nested ? NestedHeaderCell : HeaderCell;
  const width = widthOf(column2);
  const sortField = column2.sortField;
  if (!sort || !onSortChange || !sortField) {
    return /* @__PURE__ */ jsx7(Cell, { sx: width ? { width } : void 0, "aria-label": column2.kind === CLAIM_COLUMN.ACTIONS ? "Actions" : void 0, children: column2.label }, column2.key);
  }
  const active = sort.field === sortField;
  const next = active && sort.direction === SORT_DIRECTION.DESC ? SORT_DIRECTION.ASC : SORT_DIRECTION.DESC;
  return /* @__PURE__ */ jsx7(Cell, { sortDirection: active ? sort.direction : false, children: /* @__PURE__ */ jsx7(TableSortLabel, { active, direction: active ? sort.direction : SORT_DIRECTION.DESC, onClick: () => onSortChange({ field: sortField, direction: next }), children: column2.label }) }, column2.key);
}) });
var ClaimRow = memo(function ClaimRow2(props) {
  const { view, claim, programName, showRooftop, renderMoveLink, renderClaimActions, eligibilityRules } = props;
  const [open, setOpen] = useState3(false);
  const columns = columnsForRow(props);
  const ledger = claim.program_ledger ?? [];
  const estimated = isLedgerEstimated(ledger);
  const moveId = claim.move_id ?? claim.move?.id;
  const guest = claim.move?.consumer_name;
  const vin = claim.move?.vehicle_vin;
  const rooftopName = claim.move?.customer?.name || PLACEHOLDER;
  const pickup = formatDate(claim.move?.pickup_time);
  const folded = !columns.some((column2) => column2.kind === CLAIM_COLUMN.PICKUP);
  const compact = !columns.some((column2) => column2.kind === CLAIM_COLUMN.MOVE);
  const moveLink = moveId ? renderMoveLink?.(moveId, moveId) ?? moveId : PLACEHOLDER;
  const amounts = /* @__PURE__ */ jsx7(
    StackedAmounts,
    {
      view,
      estimated,
      align: compact ? "start" : "end",
      totals: view.columns.map((money) => ({ column: money, total: claimColumnTotal(money, claim) }))
    }
  );
  const eligibility = /* @__PURE__ */ jsxs3(Fragment3, { children: [
    /* @__PURE__ */ jsx7(EligibilityChip, { view, status: claim.eligibility_status, estimated: isEstimatedClaim(claim.submission_status) }),
    /* @__PURE__ */ jsx7(ClaimHoldBadge_default, { reviewHold: claim.review_hold })
  ] });
  const failedRules = failedRuleLabels(claim.evaluation_result);
  const pendingChecks = pendingRuleChecks(claim.evaluation_result);
  const eligibilityReason = failedRules.length > 0 || pendingChecks.length > 0 ? /* @__PURE__ */ jsxs3(Fragment3, { children: [
    failedRules.length > 0 && /* @__PURE__ */ jsx7(Caption, { children: failedRules.join(CAPTION_SEPARATOR) }),
    pendingChecks.length > 0 && /* @__PURE__ */ jsx7(PendingChecks, { checks: pendingChecks })
  ] }) : null;
  const submission = /* @__PURE__ */ jsx7(SubmissionChip, { status: claim.submission_status });
  const dealerView = view.key === LEDGER_VIEW.DEALER;
  const contractRules = claim.contract_id != null ? eligibilityRules?.get(Number(claim.contract_id)) : void 0;
  const cell = (column2) => {
    if (column2.kind === "money") {
      return /* @__PURE__ */ jsx7(NumericBodyCell, { children: formatLeg(view, claimColumnTotal(column2.money, claim), estimated) }, column2.key);
    }
    switch (column2.kind) {
      case CLAIM_COLUMN.TOGGLE:
        return /* @__PURE__ */ jsx7(BodyCell, { sx: { width: TOGGLE_WIDTH }, children: /* @__PURE__ */ jsx7(Toggle, { open, label: dealerView ? "eligibility" : "ledger", onClick: () => setOpen((value) => !value) }) }, column2.key);
      case CLAIM_COLUMN.CLAIM:
        return /* @__PURE__ */ jsx7(IdCell, { children: claim.id }, column2.key);
      case CLAIM_COLUMN.MOVE:
        return /* @__PURE__ */ jsx7(IdCell, { children: moveLink }, column2.key);
      case CLAIM_COLUMN.ROOFTOP:
        return /* @__PURE__ */ jsx7(BodyCell, { sx: { fontWeight: 600 }, children: rooftopName }, column2.key);
      case CLAIM_COLUMN.DETAILS:
        return /* @__PURE__ */ jsxs3(BodyCell, { children: [
          compact && /* @__PURE__ */ jsx7(Typography4, { component: "div", sx: { fontFamily: FONT_FAMILY.MONO, fontSize: FONT_SIZE.SMALL, color: ID_COLOR }, children: moveLink }),
          /* @__PURE__ */ jsx7(Typography4, { sx: { fontSize: FONT_SIZE.SMALL, fontWeight: 600 }, children: claim.move?.reference_num || PLACEHOLDER }),
          /* @__PURE__ */ jsxs3(Caption, { children: [
            describeVehicle(claim.move),
            guest ? `${CAPTION_SEPARATOR}${guest}` : ""
          ] }),
          vin && /* @__PURE__ */ jsx7(Typography4, { sx: { fontFamily: FONT_FAMILY.MONO, fontSize: FONT_SIZE.CAPTION, color: "text.secondary" }, children: vin }),
          folded && /* @__PURE__ */ jsx7(Caption, { children: [showRooftop ? rooftopName : null, pickup].filter(Boolean).join(CAPTION_SEPARATOR) }),
          compact && /* @__PURE__ */ jsxs3(Box4, { sx: { display: "flex", flexDirection: "column", gap: 0.75, mt: 1 }, children: [
            /* @__PURE__ */ jsxs3(Box4, { sx: { display: "flex", flexWrap: "wrap", gap: 0.5 }, children: [
              eligibility,
              submission
            ] }),
            eligibilityReason,
            amounts
          ] })
        ] }, column2.key);
      case CLAIM_COLUMN.ELIGIBILITY:
        return /* @__PURE__ */ jsxs3(BodyCell, { children: [
          eligibility,
          eligibilityReason
        ] }, column2.key);
      case CLAIM_COLUMN.SUBMISSION:
        return /* @__PURE__ */ jsx7(BodyCell, { children: submission }, column2.key);
      case CLAIM_COLUMN.DISTANCE:
        return /* @__PURE__ */ jsx7(NumericBodyCell, { sx: { whiteSpace: "nowrap" }, children: formatDistance(claim.move?.lane?.distance_miles) }, column2.key);
      case CLAIM_COLUMN.STATUS:
        return /* @__PURE__ */ jsx7(BodyCell, { children: /* @__PURE__ */ jsxs3(Box4, { sx: { display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 0.5 }, children: [
          eligibility,
          eligibilityReason,
          submission
        ] }) }, column2.key);
      case CLAIM_COLUMN.AMOUNTS:
        return /* @__PURE__ */ jsx7(NumericBodyCell, { children: amounts }, column2.key);
      case CLAIM_COLUMN.PICKUP:
        return /* @__PURE__ */ jsx7(BodyCell, { sx: { whiteSpace: "nowrap", color: "text.secondary", fontSize: FONT_SIZE.CAPTION }, children: pickup }, column2.key);
      case CLAIM_COLUMN.ACTIONS:
        return /* @__PURE__ */ jsx7(BodyCell, { align: "right", sx: { width: ACTIONS_WIDTH }, children: renderClaimActions?.(claim) }, column2.key);
    }
  };
  return /* @__PURE__ */ jsxs3(Fragment3, { children: [
    /* @__PURE__ */ jsx7(TableRow2, { hover: true, children: columns.map(cell) }),
    /* @__PURE__ */ jsx7(TableRow2, { children: /* @__PURE__ */ jsx7(BodyCell, { sx: { p: 0, border: 0 }, colSpan: columns.length, children: /* @__PURE__ */ jsxs3(Collapse, { in: open, timeout: "auto", unmountOnExit: true, children: [
      /* @__PURE__ */ jsx7(ClaimEligibility_default, { claim, rules: contractRules }),
      !dealerView && /* @__PURE__ */ jsx7(LedgerDetail_default, { view, ledger, programName })
    ] }) }) })
  ] });
});
var ClaimsOfRooftop = ({ rooftop, where, fetchPage, ...rowOptions }) => {
  const [claims, setClaims] = useState3([]);
  const [loading, setLoading] = useState3(true);
  const [error, setError] = useState3(null);
  const latest = useRef2(0);
  const rooftopClaimsWhere = useMemo3(() => andWhere(where, rooftopWhere(rooftop.rooftopId)), [where, rooftop.rooftopId]);
  const load = useCallback2(
    (offset) => {
      const run = ++latest.current;
      setLoading(true);
      setError(null);
      fetchPage({ where: rooftopClaimsWhere, orderBy: ROOFTOP_CLAIMS_ORDER, offset, limit: CLAIMS_SHOWN_STEP }).then(
        (page) => {
          if (run !== latest.current) return;
          setClaims((previous) => offset === 0 ? page : [...previous, ...page]);
          setLoading(false);
        },
        (failure) => {
          if (run !== latest.current) return;
          setError(failure);
          setLoading(false);
        }
      );
    },
    [fetchPage, rooftopClaimsWhere]
  );
  useEffect3(() => load(0), [load]);
  const hidden = rooftop.count - claims.length;
  return /* @__PURE__ */ jsxs3(Box4, { sx: { px: { xs: 1, sm: 2 }, py: 1, bgcolor: GREY[50] }, children: [
    /* @__PURE__ */ jsxs3(Table3, { size: "small", "aria-label": "Claims", children: [
      /* @__PURE__ */ jsx7(TableHead2, { children: /* @__PURE__ */ jsx7(TableRow2, { children: /* @__PURE__ */ jsx7(ClaimHeaderCells, { columns: columnsForRow(rowOptions), nested: true }) }) }),
      /* @__PURE__ */ jsx7(TableBody2, { children: claims.map((claim) => /* @__PURE__ */ jsx7(ClaimRow, { ...rowOptions, claim }, claim.id)) })
    ] }),
    loading && /* @__PURE__ */ jsx7(LinearProgress, { sx: { mt: 1 } }),
    Boolean(error) && /* @__PURE__ */ jsx7(Alert, { severity: "error", sx: { mt: 1 }, children: "This rooftop's claims could not be loaded. Refresh to try again." }),
    !loading && !error && hidden > 0 && /* @__PURE__ */ jsxs3(Button, { size: "small", onClick: () => load(claims.length), sx: { mt: 1, textTransform: "none" }, children: [
      "Show ",
      formatCount(Math.min(hidden, CLAIMS_SHOWN_STEP)),
      " more of ",
      formatCountOf(rooftop.count, "claim")
    ] })
  ] });
};
var ROOFTOP_LEAD_COLUMNS = 3;
var ROOFTOP_LEAD_WIDTH = 48 + 220 + 90;
var ROOFTOP_MONEY_WIDTH = 150;
var ROOFTOP_AMOUNTS_WIDTH = 220;
var ROOFTOP_COMPACT_LEAD_COLUMNS = 2;
var ProgramClaimsTable = ({ rooftops, cardWidth = null, where, fetchPage, ...rest }) => {
  const { view } = rest;
  const density = densityForWidth(cardWidth, {
    wide: ROOFTOP_LEAD_WIDTH + view.columns.length * ROOFTOP_MONEY_WIDTH,
    medium: ROOFTOP_LEAD_WIDTH + ROOFTOP_AMOUNTS_WIDTH,
    narrow: ROOFTOP_LEAD_WIDTH + ROOFTOP_AMOUNTS_WIDTH
  });
  const compact = density === DENSITY.COMPACT;
  const claimDensity = rowDensity(cardWidth, rest);
  const [toggled, toggle] = useToggleSet();
  const startsOpen = rooftops.length === 1;
  const wide = density === DENSITY.WIDE;
  const columnCount = compact ? ROOFTOP_COMPACT_LEAD_COLUMNS : ROOFTOP_LEAD_COLUMNS + (wide ? view.columns.length : 1);
  const summaries = useMemo3(
    () => new Map(
      rooftops.map((rooftop) => [
        rooftop.rooftopId,
        { estimated: rooftop.sums.estimated, totals: view.columns.map((column2) => ({ column: column2, total: column2.fromSums(rooftop.sums) })) }
      ])
    ),
    [rooftops, view]
  );
  return /* @__PURE__ */ jsx7(TableFrame, { children: /* @__PURE__ */ jsxs3(ClaimsTable, { "aria-label": "Claims by rooftop", children: [
    /* @__PURE__ */ jsx7(TableHead2, { children: /* @__PURE__ */ jsxs3(TableRow2, { children: [
      /* @__PURE__ */ jsx7(HeaderCell, { sx: { width: TOGGLE_WIDTH } }),
      /* @__PURE__ */ jsx7(HeaderCell, { children: "Rooftop" }),
      !compact && /* @__PURE__ */ jsx7(NumericHeaderCell, { children: "Claims" }),
      compact ? null : wide ? view.columns.map((column2) => /* @__PURE__ */ jsx7(NumericHeaderCell, { children: column2.label }, column2.key)) : /* @__PURE__ */ jsx7(NumericHeaderCell, { children: "Amounts" })
    ] }) }),
    /* @__PURE__ */ jsx7(TableBody2, { children: rooftops.map((rooftop) => {
      const open = startsOpen !== toggled.has(rooftop.rooftopId);
      const { estimated, totals } = summaries.get(rooftop.rooftopId);
      return /* @__PURE__ */ jsxs3(Fragment2, { children: [
        /* @__PURE__ */ jsxs3(TableRow2, { hover: true, children: [
          /* @__PURE__ */ jsx7(BodyCell, { sx: { width: TOGGLE_WIDTH }, children: /* @__PURE__ */ jsx7(Toggle, { open, label: rooftop.rooftopName, onClick: () => toggle(rooftop.rooftopId) }) }),
          /* @__PURE__ */ jsxs3(BodyCell, { sx: { fontWeight: 600 }, children: [
            rooftop.rooftopName,
            compact && /* @__PURE__ */ jsxs3(Box4, { sx: { mt: 0.5 }, children: [
              /* @__PURE__ */ jsx7(Caption, { children: formatCountOf(rooftop.count, "claim") }),
              /* @__PURE__ */ jsx7(StackedAmounts, { view, totals, estimated, align: "start" })
            ] })
          ] }),
          !compact && /* @__PURE__ */ jsx7(NumericBodyCell, { children: formatCount(rooftop.count) }),
          compact ? null : wide ? totals.map(({ column: column2, total }) => /* @__PURE__ */ jsx7(NumericBodyCell, { children: formatLeg(view, total, estimated) }, column2.key)) : /* @__PURE__ */ jsx7(NumericBodyCell, { children: /* @__PURE__ */ jsx7(StackedAmounts, { view, totals, estimated }) })
        ] }),
        /* @__PURE__ */ jsx7(TableRow2, { children: /* @__PURE__ */ jsx7(BodyCell, { sx: { p: 0, border: 0 }, colSpan: columnCount, children: /* @__PURE__ */ jsx7(Collapse, { in: open, timeout: "auto", unmountOnExit: true, children: /* @__PURE__ */ jsx7(ClaimsOfRooftop, { ...rest, density: claimDensity, rooftop, where, fetchPage }) }) }) })
      ] }, rooftop.rooftopId);
    }) })
  ] }) });
};
var ProgramClaimsTable_default = ProgramClaimsTable;

// src/ui/AllClaimsTable.tsx
import { jsx as jsx8, jsxs as jsxs4 } from "react/jsx-runtime";
var AllClaimsTable = ({ claims, sort, onSortChange, cardWidth = null, singleRooftop = false, ...options }) => {
  const withRooftop = { ...options, showRooftop: !singleRooftop };
  const rowOptions = { ...withRooftop, density: rowDensity(cardWidth, withRooftop) };
  return /* @__PURE__ */ jsx8(TableFrame, { children: /* @__PURE__ */ jsxs4(ClaimsTable, { "aria-label": "All claims", children: [
    /* @__PURE__ */ jsx8(TableHead3, { children: /* @__PURE__ */ jsx8(TableRow3, { children: /* @__PURE__ */ jsx8(ClaimHeaderCells, { columns: columnsForRow(rowOptions), sort, onSortChange }) }) }),
    /* @__PURE__ */ jsx8(TableBody3, { children: claims.map((claim) => /* @__PURE__ */ jsx8(ClaimRow, { ...rowOptions, claim }, claim.id)) })
  ] }) });
};
var AllClaimsTable_default = AllClaimsTable;

// src/ui/ClaimsFilterBar.tsx
import { useState as useState4 } from "react";
import { Badge, Box as Box5, Button as Button2, Chip as Chip3, Collapse as Collapse2, IconButton as IconButton2, InputAdornment, TextField, Tooltip as Tooltip4 } from "@mui/material";
import { keyframes } from "@mui/material/styles";
import { BiBuildings, BiFilterAlt, BiListUl, BiRefresh, BiSearch, BiX } from "react-icons/bi";
import { allTokens as allTokens3 } from "@hopdrive/ui/tokens";
import { Fragment as Fragment4, jsx as jsx9, jsxs as jsxs5 } from "react/jsx-runtime";
var SEARCH_WIDTH = 280;
var SEARCH_PLACEHOLDER = "Search claims\u2026";
var ICON_SIZE = 18;
var REFRESH_ICON_SIZE = 20;
var LAYOUT_BUTTON_SIZE = 32;
var SPIN_SECONDS = 1;
var spin = keyframes`from { transform: rotate(0deg); } to { transform: rotate(360deg); }`;
var rowSx = {
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  px: { xs: 2, sm: 3 },
  borderBottom: `1px solid ${DIVIDER}`
};
var CLAIMS_LAYOUT = {
  BY_ROOFTOP: "by_rooftop",
  ALL_CLAIMS: "all_claims"
};
var LAYOUT_OPTIONS = [
  { value: CLAIMS_LAYOUT.BY_ROOFTOP, label: "By rooftop", icon: /* @__PURE__ */ jsx9(BiBuildings, { size: ICON_SIZE }) },
  { value: CLAIMS_LAYOUT.ALL_CLAIMS, label: "All claims", icon: /* @__PURE__ */ jsx9(BiListUl, { size: ICON_SIZE }) }
];
var ClaimsFilterBar = ({ search, filters, activeFilters, loading, layout, onLayoutChange }) => {
  const [filtersOpen, setFiltersOpen] = useState4(false);
  const filterCount = activeFilters.filter((filter) => filter.key !== CLAIMS_FILTER.SEARCH).length;
  return /* @__PURE__ */ jsxs5(Fragment4, { children: [
    /* @__PURE__ */ jsxs5(Box5, { sx: { ...rowSx, justifyContent: "space-between", gap: 2, py: 2 }, children: [
      /* @__PURE__ */ jsx9(
        TextField,
        {
          size: "small",
          placeholder: SEARCH_PLACEHOLDER,
          value: search.search,
          onChange: (event) => search.setSearch(event.target.value),
          slotProps: {
            htmlInput: { "aria-label": "Search claims" },
            input: {
              startAdornment: /* @__PURE__ */ jsx9(InputAdornment, { position: "start", children: /* @__PURE__ */ jsx9(BiSearch, { size: ICON_SIZE }) })
            }
          },
          sx: {
            width: { xs: "100%", sm: SEARCH_WIDTH },
            maxWidth: "100%",
            "& .MuiOutlinedInput-root": { borderRadius: `${allTokens3.radius.md}px`, bgcolor: GREY[50] },
            "& .MuiOutlinedInput-notchedOutline": { borderColor: GREY[300] }
          }
        }
      ),
      /* @__PURE__ */ jsxs5(Box5, { sx: { display: "flex", alignItems: "center", gap: 1 }, children: [
        onLayoutChange && /* @__PURE__ */ jsx9(Box5, { role: "group", "aria-label": "Layout", sx: { display: "flex", gap: "2px", bgcolor: GREY[100], borderRadius: `${allTokens3.radius.md}px`, p: "2px" }, children: LAYOUT_OPTIONS.map((option2) => {
          const selected = option2.value === layout;
          return /* @__PURE__ */ jsx9(Tooltip4, { title: option2.label, children: /* @__PURE__ */ jsx9(
            IconButton2,
            {
              size: "small",
              "aria-label": option2.label,
              "aria-pressed": selected,
              onClick: () => onLayoutChange(option2.value),
              sx: {
                width: LAYOUT_BUTTON_SIZE,
                height: LAYOUT_BUTTON_SIZE,
                borderRadius: `${allTokens3.radius.sm}px`,
                color: selected ? "text.primary" : GREY[400],
                bgcolor: selected ? "background.paper" : "transparent",
                boxShadow: selected ? elevationShadow(allTokens3.elevation.level1) : "none",
                "&:hover": { bgcolor: selected ? "background.paper" : GREY[200] }
              },
              children: option2.icon
            }
          ) }, option2.value);
        }) }),
        /* @__PURE__ */ jsx9(Badge, { badgeContent: filterCount || void 0, color: "primary", children: /* @__PURE__ */ jsx9(
          Button2,
          {
            size: "small",
            startIcon: /* @__PURE__ */ jsx9(BiFilterAlt, { size: ICON_SIZE }),
            onClick: () => setFiltersOpen((open) => !open),
            "aria-expanded": filtersOpen,
            sx: {
              textTransform: "none",
              borderRadius: `${allTokens3.radius.md}px`,
              bgcolor: filtersOpen ? GREY[200] : GREY[100],
              color: GREY[700],
              fontWeight: 500,
              "&:hover": { bgcolor: GREY[200] }
            },
            children: "Filters"
          }
        ) }),
        /* @__PURE__ */ jsx9(Tooltip4, { title: "Refresh", children: /* @__PURE__ */ jsx9("span", { children: /* @__PURE__ */ jsx9(IconButton2, { size: "small", "aria-label": "Refresh", onClick: search.refresh, disabled: loading, sx: { color: GREY[500] }, children: /* @__PURE__ */ jsx9(
          BiRefresh,
          {
            size: REFRESH_ICON_SIZE,
            style: loading ? { animation: `${spin} ${SPIN_SECONDS}s linear infinite` } : void 0
          }
        ) }) }) })
      ] })
    ] }),
    /* @__PURE__ */ jsx9(Collapse2, { in: filtersOpen, children: /* @__PURE__ */ jsx9(Box5, { sx: { ...rowSx, gap: 2, py: 1.5 }, children: filters }) }),
    activeFilters.length > 0 && /* @__PURE__ */ jsxs5(Box5, { sx: { ...rowSx, gap: 1, py: 1 }, children: [
      activeFilters.map((filter) => /* @__PURE__ */ jsx9(
        Chip3,
        {
          label: filter.label,
          size: "small",
          onDelete: () => search.clearFilter(filter.key),
          deleteIcon: /* @__PURE__ */ jsx9(BiX, { "aria-label": `Clear ${filter.label}` }),
          sx: { borderRadius: `${allTokens3.radius.sm}px`, bgcolor: GREY[100], color: GREY[700], fontWeight: 500 }
        },
        filter.key
      )),
      /* @__PURE__ */ jsx9(
        Button2,
        {
          variant: "text",
          size: "small",
          onClick: search.clearAllFilters,
          sx: { textTransform: "none", fontSize: FONT_SIZE.CAPTION, color: GREY[600], fontWeight: 500, minWidth: "auto" },
          children: "Clear all"
        }
      )
    ] })
  ] });
};
var ClaimsFilterBar_default = ClaimsFilterBar;

// src/ui/ProgramClaimsFilters.tsx
import { FormControl, MenuItem, Select, TextField as TextField2, Typography as Typography5 } from "@mui/material";
import { Fragment as Fragment5, jsx as jsx10, jsxs as jsxs6 } from "react/jsx-runtime";
var ANY = "";
var FILTER_WIDTH = 200;
var FILTER_HEIGHT = 36;
var SUBMISSION_GROUPS = [
  { value: SUBMISSION_STATUS.NOT_SUBMITTED, label: SUBMISSION_LABELS[SUBMISSION_STATUS.NOT_SUBMITTED] },
  { value: ANY_SUBMITTED, label: ANY_SUBMITTED_LABEL }
];
var SUBMITTED_OPTIONS = SUBMITTED_STATUSES.map((value) => ({ value, label: SUBMISSION_LABELS[value] }));
var FIRST_FAILED_RULE = "ro_number";
var FAILED_RULE_OPTIONS = Object.entries(ELIGIBILITY_RULE_LABELS).filter(([value]) => !UNFILTERABLE_RULE_KEYS.has(value)).map(([value, label]) => ({ value, label })).sort((a, b) => Number(b.value === FIRST_FAILED_RULE) - Number(a.value === FIRST_FAILED_RULE));
var filterWidth = { xs: "100%", sm: FILTER_WIDTH };
var selectSx = { width: filterWidth, height: FILTER_HEIGHT, fontSize: FONT_SIZE.SMALL };
var itemSx = { fontSize: FONT_SIZE.SMALL };
var nestedItemSx = { ...itemSx, pl: 3 };
var dateFieldSx = { width: filterWidth, "& .MuiInputBase-root": { height: FILTER_HEIGHT, fontSize: FONT_SIZE.SMALL } };
var Placeholder = ({ children }) => /* @__PURE__ */ jsx10(Typography5, { component: "span", sx: { fontSize: FONT_SIZE.SMALL, color: "text.disabled" }, children });
var SingleSelectFilter = ({
  label,
  anyLabel,
  value,
  options,
  onChange,
  moreOptions
}) => /* @__PURE__ */ jsx10(FormControl, { size: "small", sx: { width: filterWidth }, children: /* @__PURE__ */ jsxs6(
  Select,
  {
    SelectDisplayProps: { "aria-label": label },
    value: value ?? ANY,
    onChange: (event) => onChange(event.target.value || null),
    displayEmpty: true,
    renderValue: (chosen) => chosen ? [...options, ...moreOptions ?? []].find((option2) => option2.value === chosen)?.label ?? chosen : /* @__PURE__ */ jsx10(Placeholder, { children: label }),
    sx: selectSx,
    children: [
      /* @__PURE__ */ jsx10(MenuItem, { value: ANY, sx: itemSx, children: anyLabel }),
      options.map((option2) => /* @__PURE__ */ jsx10(MenuItem, { value: option2.value, sx: itemSx, children: option2.label }, option2.value)),
      moreOptions?.map((option2) => /* @__PURE__ */ jsx10(MenuItem, { value: option2.value, sx: nestedItemSx, children: option2.label }, option2.value))
    ]
  }
) });
var ProgramClaimsFilters = ({ search, customers = [], sourceOptions = [] }) => {
  const customerName = (id) => customers.find((customer) => customer.id === id)?.name ?? String(id);
  return /* @__PURE__ */ jsxs6(Fragment5, { children: [
    /* @__PURE__ */ jsx10(
      SingleSelectFilter,
      {
        label: "Eligibility",
        anyLabel: "Any eligibility",
        value: search.eligibility,
        options: search.view.eligibilityOptions,
        onChange: search.setEligibility
      }
    ),
    /* @__PURE__ */ jsx10(
      SingleSelectFilter,
      {
        label: "Submission",
        anyLabel: "All submissions",
        value: search.submission,
        options: SUBMISSION_GROUPS,
        moreOptions: SUBMITTED_OPTIONS,
        onChange: search.setSubmission
      }
    ),
    /* @__PURE__ */ jsx10(
      SingleSelectFilter,
      {
        label: "Failed rule",
        anyLabel: "Any rule",
        value: search.failedRule,
        options: FAILED_RULE_OPTIONS,
        onChange: search.setFailedRule
      }
    ),
    customers.length > 1 && /* @__PURE__ */ jsx10(FormControl, { size: "small", sx: { width: filterWidth }, children: /* @__PURE__ */ jsx10(
      Select,
      {
        SelectDisplayProps: { "aria-label": "Rooftops" },
        multiple: true,
        value: search.customerIds,
        onChange: (event) => search.setCustomerIds(event.target.value),
        displayEmpty: true,
        renderValue: (ids) => {
          if (!ids.length) return /* @__PURE__ */ jsx10(Placeholder, { children: "All rooftops" });
          return ids.length === 1 ? customerName(ids[0]) : `${ids.length} rooftops`;
        },
        sx: selectSx,
        children: customers.map((customer) => /* @__PURE__ */ jsx10(MenuItem, { value: customer.id, sx: itemSx, children: customer.name }, customer.id))
      }
    ) }),
    sourceOptions.length > 0 && /* @__PURE__ */ jsx10(FormControl, { size: "small", sx: { width: filterWidth }, children: /* @__PURE__ */ jsx10(
      Select,
      {
        SelectDisplayProps: { "aria-label": "Sources" },
        multiple: true,
        value: search.sources,
        onChange: (event) => search.setSources(event.target.value),
        displayEmpty: true,
        renderValue: (sources) => sources.length ? sources.join(", ") : /* @__PURE__ */ jsx10(Placeholder, { children: "Any source" }),
        sx: selectSx,
        children: sourceOptions.map((source) => /* @__PURE__ */ jsx10(MenuItem, { value: source, sx: itemSx, children: source }, source))
      }
    ) }),
    /* @__PURE__ */ jsx10(FormControl, { size: "small", sx: { width: filterWidth }, children: /* @__PURE__ */ jsx10(
      Select,
      {
        SelectDisplayProps: { "aria-label": "Pickup date" },
        value: search.datePreset,
        onChange: (event) => search.setDatePreset(event.target.value),
        sx: selectSx,
        children: DATE_PRESET_OPTIONS.map((option2) => /* @__PURE__ */ jsx10(MenuItem, { value: option2.value, sx: itemSx, children: option2.label }, option2.value))
      }
    ) }),
    search.datePreset === DATE_PRESET.CUSTOM && /* @__PURE__ */ jsxs6(Fragment5, { children: [
      /* @__PURE__ */ jsx10(
        TextField2,
        {
          size: "small",
          type: "date",
          label: "From",
          value: search.customStart ?? "",
          onChange: (event) => search.setCustomStart(event.target.value || null),
          slotProps: { inputLabel: { shrink: true } },
          sx: dateFieldSx
        }
      ),
      /* @__PURE__ */ jsx10(
        TextField2,
        {
          size: "small",
          type: "date",
          label: "To",
          value: search.customEnd ?? "",
          onChange: (event) => search.setCustomEnd(event.target.value || null),
          slotProps: { inputLabel: { shrink: true } },
          sx: dateFieldSx
        }
      )
    ] })
  ] });
};
var ProgramClaimsFilters_default = ProgramClaimsFilters;

// src/ui/EligibilityTiles.tsx
import { Box as Box6, Card, CardActionArea, Typography as Typography6 } from "@mui/material";
import { allTokens as allTokens4 } from "@hopdrive/ui/tokens";
import { jsx as jsx11, jsxs as jsxs7 } from "react/jsx-runtime";
var { elevation, palette: palette2, radius, typography: typography2 } = allTokens4;
var TILE_GAP = "20px";
var SELECTED_BORDER = `2px solid ${palette2.secondaryAccent.main}`;
var RESTING_BORDER = "2px solid transparent";
var EligibilityTiles = ({ counts, selected, onSelect }) => /* @__PURE__ */ jsx11(Box6, { sx: { display: "flex", flexWrap: "wrap", gap: TILE_GAP }, role: "group", "aria-label": "Claims by eligibility", children: counts.map((tile) => {
  const isSelected = tile.value === selected;
  return /* @__PURE__ */ jsx11(
    Card,
    {
      sx: {
        // Two to a row on a phone, then as many as fit.
        flex: { xs: "1 1 calc(50% - 20px)", sm: "1 1 160px" },
        borderRadius: `${radius.lg}px`,
        boxShadow: elevationShadow(elevation.level2),
        border: isSelected ? SELECTED_BORDER : RESTING_BORDER,
        bgcolor: isSelected ? palette2.secondaryAccent[50] : "background.paper",
        transition: "transform 200ms ease, box-shadow 200ms ease",
        "&:hover": { transform: "translateY(-2px)", boxShadow: elevationShadow(elevation.level3) }
      },
      children: /* @__PURE__ */ jsxs7(
        CardActionArea,
        {
          "aria-pressed": isSelected,
          onClick: () => onSelect(isSelected || tile.value === null ? null : tile.value),
          sx: { p: { xs: 2, sm: 3 } },
          children: [
            /* @__PURE__ */ jsx11(Typography6, { variant: "overline", sx: { color: GREY[500], display: "block", mb: 1.5 }, children: tile.label }),
            /* @__PURE__ */ jsx11(
              Typography6,
              {
                sx: {
                  fontFamily: FONT_FAMILY.HEADING,
                  fontSize: typography2.scale.kpi.size,
                  fontWeight: typography2.scale.kpi.weight,
                  lineHeight: typography2.scale.kpi.lineHeight,
                  letterSpacing: `${typography2.scale.kpi.letterSpacing}em`,
                  color: "text.primary"
                },
                children: formatCount(tile.count)
              }
            )
          ]
        }
      )
    },
    tile.value ?? tile.label
  );
}) });
var EligibilityTiles_default = EligibilityTiles;

// src/ui/SubmissionGuide.tsx
import { Box as Box7, Typography as Typography7 } from "@mui/material";
import { BiInfoCircle } from "react-icons/bi";
import { jsx as jsx12, jsxs as jsxs8 } from "react/jsx-runtime";
var SUBMISSION_GUIDE = [
  {
    eligibility: ELIGIBILITY_STATUS.INELIGIBLE,
    submission: SUBMISSION_STATUS.NOT_SUBMITTED,
    meaning: "Will not be submitted to the program."
  },
  {
    eligibility: ELIGIBILITY_STATUS.ELIGIBLE,
    submission: SUBMISSION_STATUS.NOT_SUBMITTED,
    meaning: "Will be submitted on the next scheduled report."
  },
  {
    eligibility: null,
    submission: SUBMISSION_STATUS.SUBMITTED,
    meaning: "In a generated report, available to the program admin for processing."
  }
];
var SubmissionGuide = ({ view }) => /* @__PURE__ */ jsxs8(
  Box7,
  {
    role: "note",
    "aria-label": "What the submission statuses mean",
    sx: {
      display: "flex",
      gap: 1.5,
      px: { xs: 2, sm: 3 },
      py: 1.5,
      bgcolor: GREY[50],
      borderBottom: `1px solid ${DIVIDER}`
    },
    children: [
      /* @__PURE__ */ jsx12(Box7, { sx: { color: GREY[500], pt: "2px", flexShrink: 0 }, children: /* @__PURE__ */ jsx12(BiInfoCircle, { size: 18 }) }),
      /* @__PURE__ */ jsx12(
        Box7,
        {
          sx: { display: "grid", gridTemplateColumns: "auto auto 1fr", alignItems: "center", columnGap: 1, rowGap: 0.75 },
          children: SUBMISSION_GUIDE.map((row) => /* @__PURE__ */ jsxs8(Box7, { sx: { display: "contents" }, children: [
            /* @__PURE__ */ jsx12(Box7, { children: row.eligibility ? /* @__PURE__ */ jsx12(EligibilityChip, { view, status: row.eligibility }) : null }),
            /* @__PURE__ */ jsx12(SubmissionChip, { status: row.submission }),
            /* @__PURE__ */ jsx12(Typography7, { sx: { fontSize: FONT_SIZE.SMALL, color: "text.secondary", fontFamily: FONT_FAMILY.BODY }, children: row.meaning })
          ] }, `${row.eligibility}:${row.submission}`))
        }
      )
    ]
  }
);
var SubmissionGuide_default = SubmissionGuide;

// src/ui/useElementWidth.ts
import { useEffect as useEffect4, useState as useState5 } from "react";
function useElementWidth(ref) {
  const [width, setWidth] = useState5(null);
  useEffect4(() => {
    const element = ref.current;
    if (!element || typeof ResizeObserver === "undefined") return void 0;
    const observer = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref]);
  return width;
}

// src/ui/ProgramClaimsLedger.tsx
import { jsx as jsx13, jsxs as jsxs9 } from "react/jsx-runtime";
var ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];
var EMPTY_ICON_SIZE = 40;
var SECTION_GAP_RESPONSIVE = { xs: 2, sm: 2.5 };
var LAYOUT_STORAGE_KEY = "program-claims-view-mode";
var shadow = elevationShadow(allTokens5.elevation.level2);
var PAGE_LABEL = {
  [CLAIMS_LAYOUT.BY_ROOFTOP]: "Rooftops per page:",
  [CLAIMS_LAYOUT.ALL_CLAIMS]: "Claims per page:"
};
var readLayout = () => {
  try {
    return window.localStorage.getItem(LAYOUT_STORAGE_KEY) === CLAIMS_LAYOUT.ALL_CLAIMS ? CLAIMS_LAYOUT.ALL_CLAIMS : CLAIMS_LAYOUT.BY_ROOFTOP;
  } catch {
    return CLAIMS_LAYOUT.BY_ROOFTOP;
  }
};
var rememberLayout = (layout) => {
  try {
    window.localStorage.setItem(LAYOUT_STORAGE_KEY, layout);
  } catch {
  }
};
var NO_COLUMNS = [];
var NO_SOURCES = [];
var NO_ROOFTOPS = [];
var NO_CLAIMS = [];
var withoutColumns = (view, hide) => {
  if (hide.length === 0) return view;
  const columns = view.columns.filter((column2) => !hide.includes(column2.key));
  return columns.length === view.columns.length ? view : { ...view, columns };
};
var EmptyState = ({ filtered, onClear }) => /* @__PURE__ */ jsxs9(Box8, { sx: { display: "flex", flexDirection: "column", alignItems: "center", gap: 1, px: 3, py: 6, textAlign: "center" }, children: [
  /* @__PURE__ */ jsx13(Box8, { sx: { color: GREY[400] }, children: /* @__PURE__ */ jsx13(BiReceipt, { size: EMPTY_ICON_SIZE }) }),
  /* @__PURE__ */ jsx13(Typography8, { sx: { fontWeight: 600 }, children: filtered ? "No claims match your filters" : "No program claims in this window" }),
  /* @__PURE__ */ jsx13(Typography8, { variant: "body2", color: "text.secondary", children: filtered ? "Try a different search, or clear a filter." : "Claims and their ledger appear here once moves run for the program in the selected window." }),
  filtered && /* @__PURE__ */ jsx13(Button3, { size: "small", onClick: onClear, sx: { textTransform: "none" }, children: "Clear all filters" })
] });
function ProgramClaimsLedger({
  search,
  fetchPage,
  request,
  programName,
  customers = [],
  showClaimId = false,
  renderMoveLink,
  showEligibilityTiles = false,
  renderClaimActions,
  singleRooftop = false,
  hideUnusedColumns = NO_COLUMNS,
  eligibilityRules
}) {
  const { page, rowsPerPage, setPage, where, tilesWhere, sourceOptionsWhere, scope, refreshKey } = search;
  const [layout, setLayout] = useState6(readLayout);
  const [sort, setSort] = useState6(DEFAULT_CLAIM_SORT);
  const cardRef = useRef3(null);
  const cardWidth = useElementWidth(cardRef);
  const byRooftop = !singleRooftop && layout === CLAIMS_LAYOUT.BY_ROOFTOP;
  const pageStart = page * rowsPerPage;
  const loadUnused = useMemo4(
    () => hideUnusedColumns.length ? () => fetchUnusedColumns(request, scope, hideUnusedColumns) : null,
    [request, scope, hideUnusedColumns, refreshKey]
  );
  const unused = useServerValue(loadUnused);
  const view = useMemo4(() => withoutColumns(search.view, unused.value ?? hideUnusedColumns), [search.view, unused.value, hideUnusedColumns]);
  const summary = useServerValue(useCallback3(() => fetchClaimsSummary(request, where, tilesWhere), [request, where, tilesWhere]));
  const sourceOptions = useServerValue(useCallback3(() => fetchSourceOptions(request, sourceOptionsWhere), [request, sourceOptionsWhere]));
  const orderBy = useMemo4(() => claimsOrderBy(sort), [sort]);
  const claimsPage = useServerValue(
    useMemo4(
      () => byRooftop ? null : () => fetchPage({ where, orderBy, offset: pageStart, limit: rowsPerPage }),
      [byRooftop, fetchPage, where, orderBy, pageStart, rowsPerPage]
    )
  );
  const { customerIds } = search;
  const candidatesKey = JSON.stringify(customerIds.length ? customers.filter((customer) => customerIds.includes(customer.id)) : customers);
  const rooftopCounts = useServerValue(
    useMemo4(() => {
      if (!byRooftop) return null;
      const candidates = JSON.parse(candidatesKey);
      return () => fetchRooftopCounts(request, where, candidates).then((counts) => ({ where, counts }));
    }, [byRooftop, request, where, candidatesKey])
  );
  const rooftopSummaries = useServerValue(
    useMemo4(() => {
      const counted = rooftopCounts.value;
      if (!byRooftop || !counted) return null;
      return () => fetchRooftopSummaries(request, counted.where, counted.counts.slice(pageStart, pageStart + rowsPerPage));
    }, [byRooftop, request, rooftopCounts.value, pageStart, rowsPerPage])
  );
  const { search: term, eligibility, submission, failedRule, sources, datePreset } = search;
  const activeFilters = useMemo4(
    () => buildActiveFilters({ search: term, eligibility, submission, failedRule, customerIds, sources, datePreset, view }, customers),
    [term, eligibility, submission, failedRule, customerIds, sources, datePreset, view, customers]
  );
  const tiles = useMemo4(
    () => eligibilityTiles(view, summary.value?.beforeEligibility ?? { count: 0, byStatus: {} }),
    [view, summary.value]
  );
  const count2 = summary.value?.count ?? 0;
  const sums = summary.value?.sums;
  const totals = useMemo4(
    () => sums ? [...view.columns].sort((a, b) => Number(b.key === MONEY_COLUMN.TOTAL_MOVE_COST) - Number(a.key === MONEY_COLUMN.TOTAL_MOVE_COST)).map((column2) => ({ column: column2, total: column2.fromSums(sums) })) : [],
    [view, sums]
  );
  const changeLayout = useCallback3(
    (next) => {
      setLayout(next);
      rememberLayout(next);
      setPage(0);
    },
    [setPage]
  );
  const changeSort = useCallback3(
    (next) => {
      setSort(next);
      setPage(0);
    },
    [setPage]
  );
  const fetches = [summary, sourceOptions, unused, ...byRooftop ? [rooftopCounts, rooftopSummaries] : [claimsPage]];
  const loading = fetches.some((fetch) => fetch.loading);
  const error = fetches.find((fetch) => fetch.error)?.error ?? null;
  const rowCount = byRooftop ? rooftopCounts.value?.counts.length ?? 0 : count2;
  const firstLoad = !summary.value;
  const actions = renderClaimActions;
  const rowOptions = { view, programName, showClaimId, renderMoveLink, renderClaimActions: actions, cardWidth, eligibilityRules };
  const fetchClaims = fetchPage;
  return /* @__PURE__ */ jsxs9(Box8, { sx: { display: "flex", flexDirection: "column", gap: SECTION_GAP_RESPONSIVE }, children: [
    showEligibilityTiles && /* @__PURE__ */ jsx13(EligibilityTiles_default, { counts: tiles, selected: eligibility, onSelect: search.setEligibility }),
    /* @__PURE__ */ jsxs9(Card2, { ref: cardRef, sx: { bgcolor: "background.paper", borderRadius: `${allTokens5.radius.lg}px`, boxShadow: shadow, overflow: "visible" }, children: [
      /* @__PURE__ */ jsx13(
        ClaimsFilterBar_default,
        {
          search,
          activeFilters,
          loading,
          layout: byRooftop ? CLAIMS_LAYOUT.BY_ROOFTOP : CLAIMS_LAYOUT.ALL_CLAIMS,
          onLayoutChange: singleRooftop ? void 0 : changeLayout,
          filters: /* @__PURE__ */ jsx13(ProgramClaimsFilters_default, { search, customers, sourceOptions: sourceOptions.value ?? NO_SOURCES })
        }
      ),
      /* @__PURE__ */ jsx13(SubmissionGuide_default, { view }),
      loading && /* @__PURE__ */ jsx13(LinearProgress2, {}),
      Boolean(error) && /* @__PURE__ */ jsx13(Alert2, { severity: "error", sx: { borderRadius: 0 }, children: "The claims could not be loaded. Refresh to try again." }),
      !error && sums && count2 > 0 && /* @__PURE__ */ jsxs9(Box8, { sx: { display: "flex", flexWrap: "wrap", columnGap: 3, rowGap: 0.5, px: { xs: 2, sm: 3 }, py: 1.5, borderBottom: `1px solid ${DIVIDER}` }, children: [
        /* @__PURE__ */ jsx13(Typography8, { variant: "body2", color: "text.secondary", children: formatCountOf(count2, "claim") }),
        totals.map(({ column: column2, total }) => /* @__PURE__ */ jsxs9(Typography8, { variant: "body2", sx: { fontVariantNumeric: "tabular-nums" }, children: [
          column2.label,
          " ",
          /* @__PURE__ */ jsx13("strong", { children: formatLeg(view, total, sums.estimated) })
        ] }, column2.key))
      ] }),
      error ? null : !firstLoad && count2 === 0 ? /* @__PURE__ */ jsx13(EmptyState, { filtered: activeFilters.length > 0, onClear: search.clearAllFilters }) : /* @__PURE__ */ jsxs9(Box8, { sx: { p: { xs: 1, sm: 2 } }, children: [
        byRooftop ? /* @__PURE__ */ jsx13(ProgramClaimsTable_default, { ...rowOptions, rooftops: rooftopSummaries.value ?? NO_ROOFTOPS, where, fetchPage: fetchClaims }) : /* @__PURE__ */ jsx13(AllClaimsTable_default, { ...rowOptions, claims: claimsPage.value ?? NO_CLAIMS, sort, onSortChange: changeSort, singleRooftop }),
        /* @__PURE__ */ jsx13(Typography8, { sx: { pt: 1.5, fontSize: FONT_SIZE.CAPTION, color: "text.secondary" }, children: view.estimateNote })
      ] }),
      !error && rowCount > 0 && /* @__PURE__ */ jsx13(
        TablePagination,
        {
          component: "div",
          count: rowCount,
          page,
          rowsPerPage,
          onPageChange: (_event, next) => setPage(next),
          onRowsPerPageChange: (event) => search.setRowsPerPage(Number(event.target.value)),
          rowsPerPageOptions: ROWS_PER_PAGE_OPTIONS,
          labelRowsPerPage: PAGE_LABEL[byRooftop ? CLAIMS_LAYOUT.BY_ROOFTOP : CLAIMS_LAYOUT.ALL_CLAIMS],
          sx: { borderTop: `1px solid ${DIVIDER}` }
        }
      )
    ] })
  ] });
}
export {
  ANY_SUBMITTED,
  ClaimHoldBadge_default as ClaimHoldBadge,
  DEALER_LEDGER_VIEW,
  ELIGIBILITY_ROW_COPY,
  ELIGIBILITY_RULE_LABELS,
  ELIGIBILITY_STATUS,
  EligibilityChip,
  EligibilityRuleRows,
  HOPDRIVE_LEDGER_VIEW,
  LAYOUT_STORAGE_KEY,
  LOCATION_FIELDS,
  LOCATION_ROW_KEY,
  MONEY_COLUMN,
  PROGRAM_CHECKED_RULE_KEYS,
  PROGRAM_CHECKS,
  ProgramClaimsLedger,
  REVIEW_HOLD_LABEL,
  REVIEW_HOLD_REASON,
  RuleRow,
  SUBMISSION_LABELS,
  SUBMISSION_STATUS,
  SUBMITTED_STATUSES,
  SubmissionChip,
  atRiskRuleLabels,
  buildProgramClaimsWhere,
  claimEligibilityRows,
  describeReviewHold,
  eligibilityLabel,
  eligibilityRowLabel,
  eligibilityRuleLabel,
  eligibilityRulesByContract,
  failedRuleLabels,
  isClaimHeld,
  laterDetail,
  submissionStatusesFor,
  totalMoveCost,
  useProgramClaimsSearch
};
//# sourceMappingURL=index.mjs.map